import * as Sentry from "@sentry/nextjs"
import { Alert, Button, Label, Spinner, TextInput, Tooltip } from "flowbite-react"
import { Form, Formik, type FormikHelpers, FormikProps, FormikProvider, useFormik } from "formik"
import flatMap from "lodash/flatMap"
import has from "lodash/has"
import uniqBy from "lodash/uniqBy"
import moment from "moment"
import { extendMoment } from "moment-range"
import { useRouter } from "next/router"
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import useSWR, { useSWRConfig } from "swr"
import * as Yup from "yup"

import { BillIcon, PhoneTelephoneIcon, TwoPeopleIcon } from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import MonoDatePicker from "@components/elements/DatePicker/MonoDatePicker"
import FormGroup from "@components/elements/FormGroup"
import MonoImageGallery from "@components/elements/MonoImageGallery/MonoImageGallery"
import MonoSelect from "@components/elements/Select"
import { useDebounce } from "@hooks/useDebounce"
import useToast from "@hooks/useToast"
import CalendarIcon from "@icons/Calendar"
import InfoIcon from "@icons/Info"
import LifePreServer from "@icons/LifePreServer"
import LocationBuildings from "@icons/LocationBuildings"
import LocationMark from "@icons/LocationMark"
import RightArrow from "@icons/RightArrow"
import TicketIcon from "@icons/TicketIcon"
import type { MultiRentalProductInfoFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/MultiRentalProductInfoForm/MultiRentalProductInfoForm"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import type { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import type { CreateProductCategoryInfoRequestType } from "@mono/essentials/types/ProductTypes"
import type { GetProductLocationFormAttributes } from "@mono/essentials/types/ProductTypes"
import type { IDomain } from "@mono/essentials/types/UserTypes"
import {
  getDateInDomainTimezone,
  getDaysDifferenceBasedonDates,
  mergeFlexibleEndTimeData,
  toLocalDate,
} from "@mono/essentials/utils/Common"
import {
  checkFirstAvalibilityDate,
  fromUtcToLocalDate,
  getDomainBasedDateAndTimeFormat,
  isClosureBetweenRange,
  mapDynamicTicketTypeLabels,
  phpToMoment,
  sortAndUniqueTimeIntervals,
} from "@mono/essentials/utils/Common"
import { closureType } from "@mono/essentials/utils/Constant"
import CalendarSvg from "@public/svg/calendar.svg"
import textInputTheme from "@theme/components/textInput"

import { createDropdownOptionsForUnit } from "../../../../utils/common"
import { ProductLocation } from "../Preview/ProductionLocation"

import styles from "./RentalViewDetails.module.css"

import type React from "react"

const Moment = require("moment")

const momentRange = extendMoment(Moment)

type availabilityType = {
  start_time: string
}
interface ProductDataType extends CreateProductCategoryInfoRequestType {
  availability: availabilityType
  start_time: string
}

export interface PreviewType {
  pagePreview?: string
  selectedProduct: string | undefined
  selectedProductData: any
  setProductInfo?: any
  goToNextStep?: (
    category_id?: string,
    product_id?: string,
    product_info?: any,
    hasSingleProduct?: boolean,
    summery?: any
  ) => void
  setOrderSummery?: Dispatch<React.SetStateAction<any | undefined>>
  setShow?: Dispatch<SetStateAction<boolean>>
  isIntegratedPage?: boolean
  domainDetails: IDomain
  isSelectedDate?: Date | null
  isLoading?: boolean
  formInitialValues?: any
  editSchedulingEnabled?: boolean
  isModal?: any
  isFullPage?: boolean
  setIsFromPagePreview?: Dispatch<React.SetStateAction<boolean>>
  cartId: string
  hideQuantityEdit?: boolean
  isFromProductRentalBuyModal?: boolean
}

export interface ProductPriceTypeData {
  quantity: number
  minimum_participants_required_for_slot: number
  perCustomer?: boolean
  isMandatory?: boolean
  unitCount: number
  unitName: string | undefined
  unitId: string | undefined
  unitDescription: string
  price: string
  is_minimum_fullfilled?: boolean
  productId: string
  uuid?: string
  unitPrice: number
}

let durationOptions = []
let previousStartDate = ""

const RentalProductDetailsPreview = ({
  pagePreview,
  selectedProduct = "",
  selectedProductData,
  setProductInfo,
  goToNextStep,
  setOrderSummery,
  isIntegratedPage,
  setShow,
  isSelectedDate,
  isLoading,
  domainDetails,
  formInitialValues,
  editSchedulingEnabled = false,
  isModal,
  isFullPage,
  setIsFromPagePreview,
  cartId,
  hideQuantityEdit = false,
  isFromProductRentalBuyModel = false,
}: PreviewType) => {
  const router = useRouter()
  const [unitOptions, setUnitOptions] = useState([])
  const [submissionError, setSubmissionError] = useState<string>("")
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formError, setFormError] = useState<string>()
  const selectedProductDetail = selectedProductData ?? {}
  const [isFirstCall, setIsFirstCall] = useState(true)
  const [selectedPriceTypeData, setSelectedPriceTypeData] = useState([])

  const { showToast } = useToast()

  const { data: locationData = [] as GetProductLocationFormAttributes[], error: hasTypeError } =
    useSWR(
      router.isReady && selectedProduct && pagePreview === "with_all_details"
        ? `booking_location_${selectedProduct}-${isModal}`
        : null,
      async () => {
        try {
          const { data } = await ProductServices.getAttachedLocationOfProduct(
            selectedProduct,
            isIntegratedPage
          )
          return data
        } catch (e) {
          Sentry.captureException(e)
          console.error(e.response.data.message)
          showToast({ heading: e.response.data.message, type: "danger" })
        }
      },
      {
        revalidateOnFocus: false,
      }
    )

  const { data: unitTypesLabelData, isLoading: isUnitTypesLabelDataLoading } = useSWR(
    "unitTypesLabelData",
    async () => {
      const selectProductId = selectedProduct || ""
      const { data: getUnitTypeLabelResponse } = await OrderServices.getUnitTypeLabel(
        selectProductId,
        isIntegratedPage
      )
      if (getUnitTypeLabelResponse?.length) {
        return getUnitTypeLabelResponse[0]
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const images = selectedProductDetail?.mediaFiles?.map((image: any) => {
    return {
      original: `${image.public_url}`,
      thumbnail: `${image.public_url}`,
    }
  })

  const {
    data: selectedProductUnitTypes,
    isLoading: isProductUnitTypesLoading,
    isValidating: isProductUnitTypesLoadingValidating,
  } = useSWR(
    selectedProductDetail?.id && unitTypesLabelData
      ? `fetchSelectedProductUnitTypes-${selectedProductDetail?.id}-${isModal}-${unitTypesLabelData}`
      : null,
    async () => {
      try {
        const { data: productUnitTypes } = await ProductServices.getDynamicRentalUnitTypes(
          selectedProduct,
          isIntegratedPage
        )
        setUnitOptions(productUnitTypes)
        const optionsArray: SelectOptionType[] | [] = mapDynamicTicketTypeLabels(
          productUnitTypes,
          unitTypesLabelData
        )

        if (optionsArray?.length === 1) {
          setFieldValue("available_option", optionsArray[0].value)
          handleAvailableOption(optionsArray[0].value, productUnitTypes)
        }
        return optionsArray
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const isDefaultView = (pagePreview && pagePreview === "default_view") || false

  const handleProductPrice = async (value: number, isSelect: boolean) => {
    try {
      if (isSelect) {
        setFieldValue("quantity", value)
      } else {
        setFieldValue("quantity", values?.quantity + value)
      }
    } catch (e) {
      Sentry.captureException(e)
      console.error({ e })
    }
  }

  const multiDuration = ["Minute", "Day"]

  const getMinimumMaximumDurationMessage = () => {
    const selectedDurationDetails = (unitOptions || [])?.find(
      (item) => item?.id === values?.duration_type
    )
    const selectedDurationType =
      selectedDurationDetails?.pricing_structure_type.replace(/^per_/, "").charAt(0).toUpperCase() +
      selectedDurationDetails?.pricing_structure_type.slice(5)

    const minimumValue =
      selectedDurationDetails?.is_multi_day === 0 &&
      selectedDurationDetails?.has_flexible_timings === 0
        ? selectedDurationDetails?.duration
        : selectedDurationDetails?.minimum_duration
    const maximumValue =
      selectedDurationDetails?.is_multi_day === 0 &&
      selectedDurationDetails?.has_flexible_timings === 0
        ? selectedDurationDetails?.duration
        : selectedDurationDetails?.maximum_duration

    return (
      <span className={"text12Medium text-neutral-500"}>
        {`${
          minimumValue === maximumValue
            ? `${maximumValue} ${selectedDurationType} Fixed`
            : `${
                minimumValue > 0
                  ? `Minimum ${minimumValue ?? 0} ${selectedDurationType}${minimumValue > 1 ? "s" : ""} Required |`
                  : ""
              } ${
                maximumValue
                  ? `Maximum ${maximumValue} ${selectedDurationType}${maximumValue > 1 ? "s" : ""} Allowed`
                  : ""
              }
          `
        }
          ${
            selectedDurationDetails?.max_quantity > 0
              ? ` | Maximum ${selectedDurationDetails?.max_quantity} quantity Allowed`
              : ``
          }
        `}
      </span>
    )
  }

  const getDurationOptions = (options: any) => {
    const durationOptionsForSelect = (options || []).map((item) => {
      return {
        label: `${item?.name}`,
        value: item?.id,
      }
    })
    durationOptions = durationOptionsForSelect
    if (durationOptionsForSelect?.length === 1) {
      setFieldValue("duration_type", durationOptionsForSelect[0]?.value)
    }
  }

  const handleAvailableOption = (value?: string, options?: any) => {
    const all_unit_options = options || unitOptions
    if (value === "multi_day") {
      const durationBasedOnAvailableOptions = all_unit_options?.filter(
        (item) => item?.is_multi_day && !item.has_flexible_timings
      )
      getDurationOptions(durationBasedOnAvailableOptions)
    } else if (value === "multi_day_flex") {
      const durationBasedOnAvailableOptions = all_unit_options?.filter(
        (item) => item?.is_multi_day && item.has_flexible_timings
      )
      getDurationOptions(durationBasedOnAvailableOptions)
    } else if (value == "single_day_flex") {
      const durationBasedOnAvailableOptions = all_unit_options?.filter(
        (item) => !item?.is_multi_day && item.has_flexible_timings
      )
      getDurationOptions(durationBasedOnAvailableOptions)
    } else {
      const durationBasedOnAvailableOptions = all_unit_options?.filter(
        (item) => !item?.is_multi_day && !item.has_flexible_timings
      )
      getDurationOptions(durationBasedOnAvailableOptions)
    }
  }

  const initialValues: any = useMemo(
    () => ({
      start_date: formInitialValues?.start_date ?? "",
      end_date: formInitialValues?.end_date ?? "",
      quantity: formInitialValues?.quantity ?? 1,
      start_time: formInitialValues?.start_time ?? "",
      end_time: formInitialValues?.end_time ?? "",
      units: formInitialValues?.units ?? [],
      available_option: formInitialValues?.available_option ?? "",
      duration_type: formInitialValues?.duration_type ?? "",
      slot_id: formInitialValues?.slot_id ?? "",
      multi_day_fix_time: formInitialValues?.multi_day_fix_time ?? "",
      product_availability_id: formInitialValues?.product_availability_id ?? "",
      has_call_to_book: false,
      reopenable_reason: "",
    }),
    [formInitialValues]
  )

  const isValidDuration = (isTimeCheck = false) => {
    if (unitOptions?.length > 0) {
      const selectedDurationDetails = (unitOptions || [])?.find(
        (item) => item?.id === values?.duration_type
      )
      const selectedDurationType =
        selectedDurationDetails?.pricing_structure_type
          .replace(/^per_/, "")
          .charAt(0)
          .toUpperCase() + selectedDurationDetails?.pricing_structure_type.slice(5)
      const minimumValue =
        selectedDurationDetails?.is_multi_day === 0 &&
        selectedDurationDetails?.has_flexible_timings === 0
          ? selectedDurationDetails?.duration
          : selectedDurationDetails?.minimum_duration
      const maximumValue =
        selectedDurationDetails?.is_multi_day === 0 &&
        selectedDurationDetails?.has_flexible_timings === 0
          ? selectedDurationDetails?.duration
          : selectedDurationDetails?.maximum_duration

      if (isTimeCheck) {
        const startTime = moment(values?.start_time, "HH:mm:ss")
        const endTime = moment(values?.end_time, "HH:mm:ss")
        const durationDifference = endTime.diff(
          startTime,
          selectedDurationType === "Hour" ? "hours" : "minutes",
          true
        )
        if (
          (durationDifference >= minimumValue && durationDifference <= maximumValue) ||
          (minimumValue === 0 && maximumValue === 0)
        ) {
          setSubmissionError("")
          return true
        }
        if (durationDifference < 0) {
          setSubmissionError(`End time cannot be before start time`)
        } else if (minimumValue > 0) {
          setSubmissionError(
            `Please choose the time within range of minimum ${minimumValue} ${selectedDurationType}${
              multiDuration.includes(selectedDurationType) ? "s" : ""
            } and maximum ${maximumValue} ${selectedDurationType}${
              multiDuration.includes(selectedDurationType) ? "s" : ""
            } `
          )
        } else {
          setSubmissionError(
            `Maximum ${maximumValue} ${selectedDurationType.toLowerCase()}${
              maximumValue > 1 ? "s" : ""
            } are allowed for the booking`
          )
        }
      } else {
        const daysDifference = getDayDifference(selectedDurationType, values)
        if (
          (daysDifference >= minimumValue && daysDifference <= maximumValue) ||
          (minimumValue === 0 && maximumValue === 0)
        ) {
          setSubmissionError("")
          return true
        }
        if (minimumValue > 0 && maximumValue > 0) {
          setSubmissionError(
            `Please choose the date within range of minimum ${minimumValue} and maximum ${maximumValue} ${selectedDurationType}${
              multiDuration.includes(selectedDurationType) ? "s" : ""
            } `
          )
        } else if (minimumValue > 0) {
          setSubmissionError(
            `Minimum ${minimumValue} ${selectedDurationType.toLowerCase()}${
              minimumValue > 1 ? "s" : ""
            } are allowed for the booking`
          )
        } else if (maximumValue > 0) {
          setSubmissionError(
            `Maximum ${maximumValue} ${selectedDurationType.toLowerCase()}${
              maximumValue > 1 ? "s" : ""
            } are allowed for the booking`
          )
        }
        return false
      }
    }
    return false
  }

  const validationSchema = Yup.object().shape({
    available_option: Yup.string().required("*Available option Required"),
    duration_type: Yup.string().required("*Duration option Required"),
    start_date: Yup.string().required("*Start date Required"),
    end_date: Yup.string().when("available_option", (field, schema) => {
      return field[0]?.includes("multi_day")
        ? schema.required("*End Date Required")
        : schema.nullable()
    }),
    start_time: Yup.string().when("available_option", (field, schema) => {
      return field[0]?.includes("flex")
        ? schema.required("*Start Time Required")
        : schema.nullable()
    }),
    end_time: Yup.string().when("available_option", (field, schema) => {
      return field[0]?.includes("flex") ? schema.required("*End Time Required") : schema.nullable()
    }),
    slot_id: Yup.string().when("available_option", (field, schema) => {
      return field[0] === "single_day" ? schema.required("*Time Required") : schema.nullable()
    }),
  })

  const onSubmit = async (
    values: MultiRentalProductInfoFormAttributes,
    { setSubmitting, resetForm }: FormikHelpers<MultiRentalProductInfoFormAttributes>
  ) => {
    // if (isSlotsRequestedForBookingValid() && !formError) {
    setFormSubmitting(true)
    try {
      const { start_date, end_date, start_time, end_time, slot_id } = values

      const payload: any = values
      if (values?.available_option === "single_day") {
        payload.units = liveCapacityData
          ?.filter((item) => item.quantity > 0)
          ?.map((item) => {
            return {
              unitId: item.unit_id,
              unitCount: values?.quantity,
              start_at: `${moment(start_date).format(phpToMoment("Y-m-d"))} ${
                start_time !== "" ? start_time : "00:00:00"
              }`,
              end_at: `${moment(start_date).format(phpToMoment("Y-m-d"))} ${
                end_time !== "" ? end_time : "00:00:00"
              }`,
            }
          })
        payload.is_single_day = true
        payload.time = slot_id
      } else {
        payload.units = liveCapacityData
          ?.filter((item) => item.quantity > 0)
          ?.map((item) => {
            return {
              unitId: item.unit_id,
              unitCount: values?.quantity,
              start_at: `${moment(start_date).format(phpToMoment("Y-m-d"))} ${
                start_time !== "" ? start_time : "00:00:00"
              }`,
              end_at: `${moment(end_date || start_date).format(phpToMoment("Y-m-d"))} ${
                end_time !== "" ? end_time : "00:00:00"
              }`,
              time: values?.product_availability_id,
            }
          })
      }

      const selectedDurationDetails = (unitOptions || [])?.find(
        (item) => item?.id === values?.duration_type
      )

      const summery = {
        selectedProductDetail,
        selectedPriceTypeData: liveCapacityData?.map((item) => {
          return {
            unitName: selectedDurationDetails?.name,
            ...item,
          }
        }),
        selectedProductDurationDetails: {
          ...payload,
          unitName: selectedDurationDetails?.name,
        },
      }
      setOrderSummery?.(summery)
      setProductInfo?.({ ...payload, unitName: selectedDurationDetails?.name })
      if (isFromProductRentalBuyModel) {
        goToNextStep?.("", selectedProductDetail?.id, {
          ...payload,
          unitName: selectedDurationDetails?.name,
        })
      } else {
        goToNextStep?.(
          "",
          selectedProductDetail?.id,
          { ...payload, unitName: selectedDurationDetails?.name },
          null,
          summery
        )
      }
      setIsFromPagePreview?.(true)
      setShow?.(true)
    } catch ({ response: { status, data } }: any) {
      setFormError(
        status !== 500
          ? (data?.message ?? "Something went wrong! Please try again later")
          : "Something went wrong! Please try again later"
      )
    } finally {
      setSubmitting(false)
      // resetForm()
      setFormSubmitting(false)
    }
    // } else {
    //   return null
    // }
  }

  const activityProductInfoForm = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })

  const { values, setFieldValue, handleSubmit, getFieldProps, errors, touched } =
    activityProductInfoForm

  const [ticketBasedOnMonth, setTicketBasedOnMonth] = useState<any>({})
  const [selectedMonth, setSelectedMonth] = useState(
    formInitialValues?.start_date
      ? moment(formInitialValues?.start_date).month() + 1
      : moment().month() + 1
  )
  const [selectedYear, setSelectedYear] = useState(
    formInitialValues?.start_date ? moment(formInitialValues?.start_date).year() : moment().year()
  )

  const setMonthAndYears = (month: number, year: number, isReset = false) => {
    setSelectedMonth(month)
    setSelectedYear(year)
  }

  const {
    data: schedulingBasedOnTicket,
    isValidating: isSchedulingBasedTicketValidating,
    isLoading: isSchedulingBasedTicketLoading,
  } = useSWR(
    values.duration_type && selectedProductDetail?.id
      ? `fetchSelectedProductUnitTypes-${selectedProductDetail?.id}-${values.duration_type}-${isModal}-${isIntegratedPage}-${selectedMonth}-${selectedYear}`
      : null,
    async () => {
      try {
        const { data } = await OrderServices.getDynamicRentalscheduling(
          selectedProductDetail?.id,
          {
            unit_id: values?.duration_type,
            year: selectedYear,
            month: selectedMonth,
          },
          isIntegratedPage
        )
        // return {
        // `${selectedMonth}-${selectedYear}`= []
        // }

        const availabilitieMonth = {
          ...ticketBasedOnMonth,
          [`${selectedMonth}-${selectedYear}`]: data,
        }

        setTicketBasedOnMonth((previousData) => {
          return {
            ...previousData,
            [`${selectedMonth}-${selectedYear}`]: data,
          }
        })

        const availabilities = flatMap(availabilitieMonth, (month) => month.availabilities)
        setIsFirstCall(false)
        return {
          ...data,
          availabilities,
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const availabilities = useMemo(() => {
    const availibilityData = schedulingBasedOnTicket?.availabilities

    let excludeDates = []
    // const highLightDates = [];

    const mergedFlexibleEndTime = mergeFlexibleEndTimeData(
      availibilityData,
      moment(values.start_date).format("YYYY-MM-DD")
    )

    const mergedFlexibleStartTime = availibilityData?.reduce((acc, availability) => {
      const available_slot_dates =
        availability?.available_slot_start_dates?.map((item) => item?.slot_local_date) ?? []
      if (
        values?.start_date &&
        !available_slot_dates.includes(moment(values?.start_date).format("YYYY-MM-DD"))
      ) {
        return acc
      }
      return acc.concat(
        availability.flexible_time_range.flexible_from_time.map((time) => ({
          product_availability_id: availability.product_availability_id,
          ...time,
        }))
      )
    }, [])

    const mergedFixedSlots = availibilityData?.reduce((acc, availability) => {
      return acc.concat(
        availability.fixed_slots.map((slot) => ({
          product_availability_id: availability.product_availability_id,
          ...slot,
        }))
      )
    }, [])

    const highLightDates = availibilityData
      ?.reduce((acc: any, availability: any) => {
        return acc.concat(
          availability?.available_slot_start_dates
            ?.map((item: any) => item?.slot_local_date)
            ?.map((slot: any) => toLocalDate(slot))
            ?.filter((dateString: any) => moment(dateString).isSameOrAfter(moment().startOf("day")))
        )
      }, [])
      .sort((a: any, b: any) => moment(a).diff(moment(b)))

    let localStartDate = moment()
    let localEndDate = moment()
    const pAvailablity = {}

    availibilityData?.map((item) => {
      const lStartDate = moment(
        moment(item?.availability_range?.local_from_date).format("YYYY-MM-DD")
      )
      const lEndDate = moment(moment(item?.availability_range?.local_to_date).format("YYYY-MM-DD"))
      if (lStartDate?.isSameOrBefore(localStartDate)) {
        localStartDate = lStartDate
      }
      if (lEndDate?.isSameOrAfter(localEndDate)) {
        localEndDate = lEndDate
      }
      pAvailablity[item?.product_availability_id] = [
        ...(pAvailablity ? (pAvailablity[item?.product_availability_id] ?? []) : []),
        ...item?.available_slot_end_dates?.map((item) => item?.slot_local_date),
      ]
    })

    // short date from  low to high
    highLightDates?.sort((a, b) => a - b)

    if (highLightDates?.length) {
      const stDate = moment(highLightDates[0])
      if (localStartDate?.isSameOrBefore(moment(stDate))) {
        localStartDate = stDate?.subtract(1, "days")
      }
    }

    const currentDate = moment(localStartDate?.format("YYYY-MM-DD"))
    // Create a moment range between currentDate and localEndDate
    const rangeDates = momentRange?.range(currentDate, localEndDate)

    // Get all dates in array between currentDate and localEndDate
    const allDatesBetweenStartEndDate: moment.Moment[] = Array.from(rangeDates?.by("day"))
    const highLightMomentDates = highLightDates?.map((date) => moment(date)?.format("YYYY-MM-DD"))
    const allExcludeDates = allDatesBetweenStartEndDate?.filter((dateString) => {
      const date = moment(dateString)
      return (
        date.isBefore(localStartDate) || !highLightMomentDates?.includes(date?.format("YYYY-MM-DD"))
      ) // Filter dates before current date
    })
    excludeDates = allExcludeDates?.map((item) => toLocalDate(item))

    const closures = availibilityData?.map((item) => item.closures)?.flat()

    return {
      availability_range: {
        from_date: localStartDate?.format("YYYY-MM-DD"),
        to_date: localEndDate?.format("YYYY-MM-DD"),
      },
      flexible_time_range: {
        flexible_end_time: mergedFlexibleEndTime,
        flexible_from_time: mergedFlexibleStartTime,
      },
      fixed_slots: mergedFixedSlots,
      highLightDates,
      excludeDates,
      ...pAvailablity,
      availibilityData,
      closures,
    }
  }, [ticketBasedOnMonth, values?.start_date, schedulingBasedOnTicket])

  const debouncedUnits = useDebounce(values?.quantity, 500)

  const onEndDateSelect = (selectedDate) => {
    setFieldValue("reopenable_reason", "")
    setFieldValue("has_call_to_book", false)
    setFieldValue("quantity", 1)
    const availibilityData = schedulingBasedOnTicket?.availabilities

    const end_date = selectedDate
    const startDate = values?.start_date
    for (const availibility of availibilityData || []) {
      const avlCheck = availabilities[availibility.product_availability_id]
      if (
        avlCheck?.includes(moment(end_date).format("YYYY-MM-DD")) &&
        avlCheck?.includes(moment(startDate).format("YYYY-MM-DD"))
      ) {
        setFieldValue("product_availability_id", availibility.product_availability_id)
        break
      }
    }
    setFieldValue("end_date", selectedDate)
    setSubmissionError("")
  }

  const isClosureBetweenDateRange = useMemo(() => {
    const endDate = values?.end_date
    const startDate = values?.start_date
    return false && isClosureBetweenRange(startDate, endDate, availabilities?.excludeDates)
  }, [])

  const endDates = useMemo(() => {
    const availibilityData = schedulingBasedOnTicket?.availabilities

    // STEP FUNCTION FOR END DATES
    const selectedDurationDetails = (unitOptions || [])?.find(
      (item) => item?.id === values?.duration_type
    )

    const daysToSelectable = []

    const ticketType = selectedDurationDetails?.pricing_structure_type
    const ticketMinimumDuration =
      selectedDurationDetails?.minimum_duration > 0 ? selectedDurationDetails?.minimum_duration : 1
    const ticketMaximumDuration =
      selectedDurationDetails?.maximum_duration > 0 ? selectedDurationDetails?.maximum_duration : 1
    const countBasedOnNextDays = !selectedDurationDetails?.is_calculation_based_on_calendar_days
    let lastDate = values?.start_date
      ? moment(values?.start_date).add(1 * ticketMaximumDuration, "day")
      : moment()

    if (
      !values?.start_date ||
      selectedDurationDetails?.minimum_duration === selectedDurationDetails?.maximum_duration
    ) {
      return {
        highLightDates: availabilities?.highLightDates || [],
        excludeDates: availabilities?.excludeDates,
      }
    }
    if (countBasedOnNextDays) {
      lastDate = lastDate.add(1, "day")
    }

    const ticketTypeToCalculateStep = ["per_week", "per_month", "per_year"]
    let filteredEndDate = false
    if (ticketTypeToCalculateStep.includes(ticketType) && values?.start_date) {
      let typeOfDayToAddInSteps = "week"
      let stepAddition = 1
      if (ticketType === "per_month") {
        typeOfDayToAddInSteps = "M"
        stepAddition = 1
      } else if (ticketType === "per_year") {
        typeOfDayToAddInSteps = "year"
        stepAddition = 1
      }
      let currentDate = moment(values?.start_date)
      const localEndDate = moment(availabilities?.availability_range?.to_date)
      lastDate = moment(currentDate).add(
        stepAddition * ticketMaximumDuration,
        typeOfDayToAddInSteps as any
      )

      if (countBasedOnNextDays) {
        lastDate = lastDate.add(1, "day")
      }

      while (currentDate.isSameOrBefore(localEndDate, typeOfDayToAddInSteps as any)) {
        // Add the required number of periods to the current date
        let endDateOfPeriod
        if (ticketType === "per_month") {
          if (countBasedOnNextDays) {
            endDateOfPeriod = moment(currentDate).add(stepAddition * ticketMinimumDuration, "month")
          } else {
            endDateOfPeriod = moment(currentDate)
              .add(stepAddition * ticketMinimumDuration, "month")
              .subtract(1, "day")
          }
        } else if (ticketType === "per_year") {
          if (countBasedOnNextDays) {
            endDateOfPeriod = moment(currentDate).add(stepAddition * ticketMinimumDuration, "year")
          } else {
            endDateOfPeriod = moment(currentDate)
              .add(stepAddition * ticketMinimumDuration, "year")
              .subtract(1, "day")
          }
        } else {
          if (countBasedOnNextDays) {
            endDateOfPeriod = moment(currentDate).add(stepAddition * ticketMinimumDuration, "week")
          } else {
            endDateOfPeriod = moment(currentDate)
              .add(stepAddition * ticketMinimumDuration, "week")
              .subtract(1, "day")
          }
        }
        daysToSelectable.push(moment(endDateOfPeriod).format("YYYY-MM-DD"))
        currentDate = moment(currentDate).add(
          stepAddition * ticketMinimumDuration,
          typeOfDayToAddInSteps as any
        )
        filteredEndDate = true
      }
    }

    const start_date = values?.start_date
    const highLight = availibilityData?.flatMap((item) => {
      const avlCheck = availabilities[item.product_availability_id]
      if (avlCheck?.includes(moment(start_date).format("YYYY-MM-DD"))) {
        return avlCheck
      }
      return []
    })

    let highLightDates = highLight?.map((date) => toLocalDate(date)) || []
    if (ticketTypeToCalculateStep.includes(ticketType) && values?.start_date) {
      highLightDates = highLightDates.filter((date) => {
        const momentDate = moment(date)
        return momentDate.isBetween(start_date, lastDate, null, "[]")
      })
    } else {
      // Calculate end date based on ticketMaximumDuration
      const endDate = start_date.clone().add(ticketMaximumDuration, "days")
      highLightDates = highLightDates.filter((date) =>
        moment(date).isBetween(start_date, endDate, null, "[]")
      )
    }

    let excludeDates = [...availabilities?.excludeDates]
    const currentDate = moment(availabilities?.availability_range?.from_date)
    const localEndDate = moment(availabilities?.availability_range?.to_date)

    // Create a moment range between currentDate and localEndDate

    const rangeDates = momentRange.range(currentDate, localEndDate)

    // Get all dates in array between currentDate and localEndDate

    const allDatesBetweenStartEndDate: moment.Moment[] = Array.from(rangeDates.by("day"))

    // Function to check if a date is not included in highlightDates
    const notInHighlightDates = (date: any) =>
      !highLightDates?.some((highlightDate) => moment(date)?.isSame(highlightDate, "day"))

    // Filter out dates not in highlightDates
    const allExcludeDates = allDatesBetweenStartEndDate?.filter((date) => notInHighlightDates(date))
    excludeDates = allExcludeDates?.map((item) => toLocalDate(item))

    return {
      highLightDates,
      excludeDates,
    }
  }, [values?.start_date, ticketBasedOnMonth])

  const handleStartDateSelection = (selectedDate: any, isMultiDayFixed: boolean) => {
    setFieldValue("start_date", selectedDate)
    setFieldValue("end_date", "")
    setFieldValue("start_time", "")
    setFieldValue("end_time", "")
    setFieldValue("slot_id", "")
    if (
      selectedDurationDetails?.maximum_duration === selectedDurationDetails?.minimum_duration &&
      !values?.available_option?.includes("single_day")
    ) {
      handleEndDateSelection(selectedDurationDetails, selectedDate)
    }
  }

  const {
    data: liveCapacityData = [],
    isLoading: isLiveCapacityDataLoading,
    isValidating: capacityLoading,
    error: liveCapacityError,
    mutate: refetchLiveCapacityData,
  } = useSWR(
    schedulingBasedOnTicket?.selected_ticket &&
      selectedProductDetail?.id &&
      values?.start_date &&
      values.duration_type &&
      !values?.has_call_to_book
      ? `liveCapacityData-${isModal}-${values.duration_type}-${values?.product_availability_id}-${schedulingBasedOnTicket?.selected_ticket}`
      : null,
    async () => {
      try {
        setSubmissionError("")
        if (schedulingBasedOnTicket) {
          const { start_date, end_date, start_time, end_time, slot_id } = values

          const payload: any = {
            quantities: [
              {
                unit_id: values.duration_type,
                quantity: values?.quantity ?? 0,
              },
            ],
            existing_cart_id: cartId ?? "",
          }

          if (values?.available_option.includes("multi_day")) {
            if (values?.available_option === "multi_day_flex") {
              if (!start_time || !end_time) {
                return
              }
              ;(payload.start_at = `${start_date.format("YYYY-MM-DD")} ${start_time}`),
                (payload.end_at = `${end_date.format("YYYY-MM-DD")} ${end_time}`)
            } else {
              if (!start_date || !end_date || !start_time || !end_time) {
                return
              }
              ;(payload.start_at = `${start_date.format("YYYY-MM-DD")} ${start_time}`),
                (payload.end_at = `${end_date.format("YYYY-MM-DD")} ${end_time}`)
            }

            if (!values?.product_availability_id) {
              return
            }
            payload.product_availability_id = values?.product_availability_id

            if (isValidDuration(false)) {
              const { data } = await OrderServices.getDynamicRentalLiveCapacity(
                selectedProductDetail?.id,
                payload,
                isIntegratedPage
              )
              setSelectedPriceTypeData(data)
              setOrderSummery?.((prev) => {
                return {
                  ...prev,
                  selectedPriceTypeData: data,
                }
              })
              return data
            }
          } else if (values?.available_option.includes("single_day")) {
            if (values?.available_option === "single_day_flex") {
              if (!start_time || !end_time) {
                return
              }
              if (!isValidDuration(true)) {
                return
              }
              ;(payload.start_at = `${moment(start_date).format("YYYY-MM-DD")} ${start_time}`),
                (payload.end_at = `${moment(start_date).format("YYYY-MM-DD")} ${end_time}`)
            } else if (values?.available_option === "single_day") {
              const selectedTime = availabilities.fixed_slots?.find(
                (item) => item.slot_id === slot_id
              )
              if (!slot_id || !selectedTime) {
                return
              }
              ;(payload.start_at = `${moment(start_date).format("YYYY-MM-DD")} ${
                selectedTime?.local_start_date.split(" ")[1]
              }`),
                (payload.end_at = `${moment(start_date).format("YYYY-MM-DD")} ${
                  selectedTime?.local_end_date.split(" ")[1]
                }`)
            }
            if (!values?.product_availability_id) {
              return
            }
            payload.product_availability_id = values?.product_availability_id

            const isOutOfClosureRange = isRangeFree(
              payload?.start_at,
              payload.end_at,
              availabilities?.closures || []
            )
            if (isOutOfClosureRange) {
              const { data } = await OrderServices.getDynamicRentalLiveCapacity(
                selectedProductDetail?.id,
                payload,
                isIntegratedPage
              )
              setSelectedPriceTypeData(data)
              setOrderSummery?.((prev) => {
                return {
                  ...prev,
                  selectedPriceTypeData: data,
                }
              })
              return data
            }
          }
        }
      } catch (e) {
        Sentry.captureException(e)
        console.log("e: ", e)
        setSubmissionError(e?.response?.data?.message ?? "Something went wrong! please try again")
        // showToast({ heading: e?.response?.data?.message ?? 'Something went wrong! please try again', type: 'danger'})
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const unAvailableDate = () => {
    const startDate = moment(toLocalDate(availabilities?.availability_range?.from_date))
    const currentDate = moment(toLocalDate())
    if (startDate.isSameOrAfter(currentDate)) {
      return startDate.toDate()
    }
    return currentDate.toDate()
  }

  const handleScrollInToViewFunction = () => {
    const element = document.getElementById("booking-container")
    element.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    })
  }

  useEffect(() => {
    return () => {
      previousStartDate = ""
    }
  }, [])

  useEffect(() => {
    const selectedDurationDetails = (unitOptions || [])?.find(
      (item) => item?.id === values?.duration_type
    )
    let newStartDate = ""
    if (values?.start_date) {
      newStartDate = moment(values?.start_date).format("YYYY-MM-DD")
    }
    const isStartDateDiffer = Boolean(
      selectedDurationDetails?.minimum_duration === selectedDurationDetails?.maximum_duration &&
        previousStartDate &&
        !moment(newStartDate).isSame(previousStartDate)
    )
    if (
      selectedDurationDetails?.minimum_duration !== selectedDurationDetails?.maximum_duration ||
      isStartDateDiffer ||
      values?.available_option.includes("single_day")
    ) {
      refetchLiveCapacityData()
    }
    if (
      selectedDurationDetails?.minimum_duration === selectedDurationDetails?.maximum_duration &&
      values?.start_date
    ) {
      previousStartDate = moment(values?.start_date).format("YYYY-MM-DD").toString()
      refetchLiveCapacityData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedUnits,
    values.slot_id,
    values.start_date,
    values.start_time,
    values.end_date,
    values.end_time,
  ])

  useEffect(() => {
    if (formInitialValues.start_date && unitOptions?.length > 0 && schedulingBasedOnTicket) {
      refetchLiveCapacityData()
      if (!values?.duration_type) {
        handleAvailableOption(formInitialValues?.available_option)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitOptions, editSchedulingEnabled, schedulingBasedOnTicket])

  const getDayDifference = (type, values) => {
    const selectedDurationDetails = (unitOptions || [])?.find(
      (item) => item?.id === values?.duration_type
    )
    const startDate = !selectedDurationDetails?.is_calculation_based_on_calendar_days
      ? values?.start_date.clone().startOf("day").add(1, "day")
      : values?.start_date.startOf("day")
    const endDate =
      !values?.available_option?.includes("single_day") &&
      values?.end_date &&
      moment(toLocalDate(values?.end_date))
        ? values?.end_date.endOf("day")
        : values?.start_date.endOf("day")

    // For time-based calculations
    if (values?.start_time && values?.end_time) {
      const startTime = moment(values?.start_time, "HH:mm")
      const endTime = moment(values?.end_time, "HH:mm")

      if (endTime.isBefore(startTime)) {
        endTime.add(1, "day")
      }

      const diffInMinutes = endTime.diff(startTime, "minutes")
      const diffInHours = endTime.diff(startTime, "hours")

      if (type === "Minute") {
        return Math.max(1, Math.ceil(diffInMinutes))
      } else if (type === "Hour") {
        return Math.max(1, Math.ceil(diffInHours))
      }
    }

    let dayDifference

    if (type === "Week") {
      const diffInDays = getDaysDifferenceBasedonDates(startDate, endDate)
      dayDifference = Math.max(1, Math.ceil(diffInDays / 7))
    } else if (type === "Month") {
      const diffInMonths = endDate.diff(startDate, "months", true)
      dayDifference = Math.max(1, Math.ceil(diffInMonths))
    } else if (type === "Year") {
      const diffInYears = endDate.diff(startDate, "years", true)
      dayDifference = Math.max(1, Math.ceil(diffInYears))
    } else {
      dayDifference = getDaysDifferenceBasedonDates(startDate, endDate)
      if (dayDifference === 0 && values?.end_date) {
        const durationDifference = endDate.diff(startDate, "minutes", true)
        if (durationDifference > 0) {
          dayDifference = 1
        }
      }
    }

    return dayDifference
  }

  const getDaysDifferenceBasedonDates = (startDate, endDate) => {
    return endDate.diff(startDate, "days") + 1
  }

  const handleTicketCounter = () => {
    const selectedDurationDetails = (unitOptions || [])?.find(
      (item) => item?.id === values?.duration_type
    )
    return (
      <div>
        {liveCapacityData?.length > 0 && (
          <>
            <div className={"my-5 border-b border-neutral-200"} />
            <div
              className={
                "py-2.5 px-3 bg-primary-200 flex items-center gap-4 rounded-md border border-primary-400"
              }
            >
              <div>
                <TicketIcon className={"h-5 w-5"} />
              </div>
              <div>
                <div
                  className={"text14Semibold text-neutral-900"}
                >{`${liveCapacityData[0]?.formatted_unit_price}`}</div>
                {values?.available_option !== "single_day" ? (
                  <>{getMinimumMaximumDurationMessage()}</>
                ) : null}
              </div>
            </div>
            <div className={"mt-4 mb-1"}>
              {domainDetails?.quantity_indicator === "increment_decrement_button" ? (
                <div
                  className={`${
                    selectedProductDetail?.widget_quantity_selector_label ? "" : "flex"
                  } items-center gap-2`}
                >
                  <div className={"text14Medium mb-2"}>
                    {selectedProductDetail?.widget_quantity_selector_label
                      ? selectedProductDetail.widget_quantity_selector_label
                      : "QTY."}
                  </div>
                  <div
                    className={"flex items-center gap-3 mb-1.5"}
                    data-testid={"quantity-selector"}
                  >
                    <div>
                      <button
                        type={"button"}
                        tabIndex={-1}
                        className={`${values.quantity === 0 && "cursor-not-allowed"}`}
                        onClick={() => {
                          handleProductPrice(-1, false)
                        }}
                        disabled={values.quantity === 0}
                      >
                        <span
                          className={`${
                            values.quantity === 0
                              ? "text-primary-400 cursor-not-allowed bg-primary-200"
                              : "text-primary-600 bg-primary-300"
                          } m-auto block h-8 w-8 text-xl rounded-md`}
                        >
                          −
                        </span>
                      </button>
                    </div>
                    <div
                      className={
                        "flex h-8 w-12 text-neutral-1000 items-center rounded-md justify-center border border-neutral-neutral-450 px-1 py-1.5"
                      }
                    >
                      <div className={"text15Medium"}>{values.quantity}</div>
                    </div>
                    <div>
                      <button
                        type={"button"}
                        tabIndex={-1}
                        disabled={
                          values.quantity >= liveCapacityData[0]?.total_slot_availability ||
                          isLiveCapacityDataLoading ||
                          (selectedDurationDetails?.max_quantity > 0 &&
                            values?.quantity >= selectedDurationDetails?.max_quantity)
                        }
                        onClick={() => {
                          handleProductPrice(1, false)
                        }}
                      >
                        <span
                          className={`${
                            values.quantity >= liveCapacityData[0]?.total_slot_availability ||
                            isLiveCapacityDataLoading ||
                            (selectedDurationDetails?.max_quantity > 0 &&
                              values?.quantity >= selectedDurationDetails?.max_quantity)
                              ? "text-primary-400 cursor-not-allowed bg-primary-200"
                              : "text-primary-600 bg-primary-300"
                          } m-auto block h-8 w-8 rounded-md text-xl`}
                        >
                          +
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <FormGroup
                  label={selectedProductDetail?.widget_quantity_selector_label ?? ""}
                  name={"quantity"}
                  labelClassName={"block"}
                >
                  <MonoSelect
                    options={createDropdownOptionsForUnit(
                      liveCapacityData[0]?.maximum_capacity_required_for_unit ||
                        liveCapacityData[0]?.total_slot_availability
                    )}
                    {...getFieldProps("quantity")}
                    onValueChange={(newValue) => {
                      handleProductPrice(newValue?.value, true)
                    }}
                    menuPosition={"fixed"}
                    isSearchable={false}
                    isFromWidget
                    // containerBackground={'#2F69FE'}
                    // selectedValueColor={'#FFFFFF'}
                  />
                </FormGroup>
              )}
            </div>
            {!isDefaultView ? (
              <>
                <div className={"my-2 border-b border-neutral-200"} />
                <div className={"my-4 flex items-center justify-between"}>
                  <div className={"text14Medium"}>
                    {`${liveCapacityData[0]?.formatted_unit_price ?? 0} X ${values.quantity} Qty.`}
                  </div>
                  <div className={"orderTotal"}>{liveCapacityData[0]?.formatted_price ?? 0}</div>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    )
  }

  const singleDayFixedTimeSlot = useMemo(() => {
    const slots = availabilities?.fixed_slots
      ?.filter((item) => {
        return item?.local_start_date?.includes(
          values?.start_date ? values?.start_date.format("YYYY-MM-DD") : ""
        )
      })
      .map((item) => {
        return {
          label:
            item?.reopenable_for === closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
              ? `${item?.local_start_time}-${item?.local_end_time}  -- Select For availibilities`
              : `${item?.local_start_time}-${item?.local_end_time}`,
          value: item?.slot_id,
          product_availability_id: item?.product_availability_id,
          reopenable_reason: item?.reopenable_reason,
          reopenable_for: item?.reopenable_for,
        }
      })
    if (slots?.length === 1) {
      setFieldValue("reopenable_reason", slots[0]?.reopenable_reason)
      setFieldValue("quantity", 1)
      if (slots[0]?.reopenable_for === closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL) {
        setFieldValue("has_call_to_book", true)
      } else {
        setFieldValue("has_call_to_book", false)
        setFieldValue("slot_id", slots[0]?.value)
        setFieldValue("product_availability_id", slots[0]?.product_availability_id)
      }
    }

    return sortAndUniqueTimeIntervals(slots ?? [])
  }, [availabilities, values.start_date])

  const handleEndDateSelection = (selectedDurationDetails, selectedDate: any) => {
    const countBasedOnNextDays = !selectedDurationDetails?.is_calculation_based_on_calendar_days
    const selectedStartDate = selectedDate.clone().startOf()
    let clonedEndDate
    if (selectedDurationDetails?.pricing_structure_type === "per_month") {
      if (countBasedOnNextDays) {
        clonedEndDate = selectedStartDate.add(selectedDurationDetails?.maximum_duration, "month")
      } else {
        clonedEndDate = selectedStartDate
          .add(selectedDurationDetails?.maximum_duration, "month")
          .subtract(1, "day")
      }
    } else if (selectedDurationDetails?.pricing_structure_type === "per_week") {
      if (countBasedOnNextDays) {
        clonedEndDate = selectedStartDate.add(selectedDurationDetails?.maximum_duration, "weeks")
      } else {
        clonedEndDate = selectedStartDate
          .add(selectedDurationDetails?.maximum_duration, "weeks")
          .subtract(1, "day")
      }
    } else if (selectedDurationDetails?.pricing_structure_type === "per_year") {
      if (countBasedOnNextDays) {
        clonedEndDate = selectedStartDate.add(selectedDurationDetails?.maximum_duration, "year")
      } else {
        clonedEndDate = selectedStartDate
          .add(selectedDurationDetails?.maximum_duration, "year")
          .subtract(1, "day")
      }
    } else {
      if (countBasedOnNextDays) {
        clonedEndDate = selectedStartDate.add(selectedDurationDetails?.maximum_duration, "day")
      } else {
        clonedEndDate = selectedStartDate
          .add(selectedDurationDetails?.maximum_duration, "day")
          .subtract(1, "day")
      }
    }
    onEndDateSelect(clonedEndDate)
  }

  const getAvailibilitiesTimes: any = useMemo(() => {
    if (
      availabilities?.flexible_time_range?.flexible_from_time?.length > 0 &&
      availabilities?.flexible_time_range?.flexible_end_time?.length > 0
    ) {
      const startTimeMoment = moment(values?.start_time, "HH:mm:ss")
      const selectedDurationDetails = (unitOptions || [])?.find(
        (item) => item?.id === values?.duration_type
      )
      const selectedDurationType =
        selectedDurationDetails?.pricing_structure_type.replace(/^per_/, "").charAt(0) +
        selectedDurationDetails?.pricing_structure_type.slice(5)

      const startTimes = availabilities?.flexible_time_range?.flexible_from_time?.map((item) => {
        return {
          label: item?.local_time,
          value: item?.local_start_time,
          product_availability_id: item?.product_availability_id,
        }
      })

      if (startTimes?.length === 1 && values?.start_date) {
        setFieldValue("start_time", startTimes[0]?.value)
        setFieldValue("product_availability_id", startTimes[0]?.product_availability_id)
      }
      const productAvailibilityId =
        startTimes?.length === 1
          ? startTimes[0]?.product_availability_id
          : values?.product_availability_id
      const endTimes = availabilities?.flexible_time_range?.flexible_end_time
        ?.filter((item) => item.product_availability_id === productAvailibilityId)
        ?.map((item) => {
          return {
            label: item?.local_time,
            value: item?.local_end_time,
          }
        })

      let filterEndTimes
      if (
        selectedDurationDetails?.maximum_duration === selectedDurationDetails?.minimum_duration &&
        values?.start_time
      ) {
        const EndTime = startTimeMoment
          ?.add(selectedDurationDetails?.maximum_duration, selectedDurationType)
          ?.format("HH:mm:ss")
        filterEndTimes = endTimes?.length && endTimes?.filter((item) => item?.value === EndTime)
      } else {
        filterEndTimes = endTimes.filter((time) => {
          const endTime = moment(time.value, "HH:mm:ss")
          return (
            endTime.isAfter(startTimeMoment) &&
            endTime.diff(startTimeMoment, selectedDurationType) <=
              selectedDurationDetails?.maximum_duration
          )
        })
      }

      if (filterEndTimes?.length === 1 && values?.start_date) {
        setFieldValue("end_time", filterEndTimes[0]?.value)
      }

      return {
        startTimes,
        endTimes: filterEndTimes || [],
      }
    }
    return []
  }, [availabilities, values?.product_availability_id, values])

  const multiDayFixedTimeSlot = useMemo(() => {
    if (!values?.start_date) return []

    const startDateStr = moment(values?.start_date).format("YYYY-MM-DD")

    const listOfTotalSlots = availabilities?.availibilityData?.map((item) => {
      const matchingSlot = item?.fixed_slots?.find(
        (slot) => moment(slot?.local_start_date).format("YYYY-MM-DD") === startDateStr
      )

      return matchingSlot // slot time display based on start date scheduling
        ? {
            ...matchingSlot,
            product_availability_id: item?.product_availability_id,
          }
        : []
    })

    const uniqueSlots = uniqBy(listOfTotalSlots?.flat(), "product_availability_id")

    const slotList = uniqueSlots?.map((item) => {
      return {
        label:
          item?.reopenable_for === closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
            ? `${item?.local_start_time}-${item?.local_end_time}  -- Select For availibilities`
            : `${item?.local_start_time}-${item?.local_end_time}`,
        value: item?.product_availability_id,
        start_time: item?.local_start_date?.split(" ")?.pop(),
        end_time: item?.local_end_date?.split(" ")?.pop(),
        reopenable_reason: item?.reopenable_reason,
        reopenable_for: item?.reopenable_for,
      }
    })
    if (slotList?.length === 1) {
      setFieldValue("reopenable_reason", slotList[0]?.reopenable_reason)
      if (
        slotList[0]?.reopenable_for === closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
      ) {
        setFieldValue("has_call_to_book", true)
      } else {
        setFieldValue("has_call_to_book", false)
        setFieldValue("multi_day_fix_time", slotList[0]?.value)
        setFieldValue("start_time", slotList[0]?.start_time)
        setFieldValue("end_time", slotList[0]?.end_time)
        setFieldValue("product_availability_id", slotList[0]?.value)
      }
    }

    return slotList
  }, [availabilities, values.start_date, values?.end_date])

  const selectedDurationDetails = useMemo(() => {
    return (unitOptions || [])?.find((item) => item?.id === values?.duration_type)
  }, [unitOptions, values?.duration_type, formInitialValues])

  const isSlotsRequestedForBookingValid = () => {
    return values?.quantity > 0 && liveCapacityData?.length > 0
  }

  const handleDayDifference = () => {
    const selectedDurationType =
      selectedDurationDetails?.pricing_structure_type.replace(/^per_/, "").charAt(0).toUpperCase() +
      selectedDurationDetails?.pricing_structure_type.slice(5)

    const dayDifferenceCount = getDayDifference(selectedDurationType, values)

    return (
      <>{`${Number.parseInt(dayDifferenceCount)} ${selectedDurationType}${dayDifferenceCount > 1 ? "s" : ""}`}</>
    )
  }

  const isRangeFree = (startAt, endAt, closures) => {
    if (
      !moment(startAt, "YYYY-MM-DD HH:mm:ss", true).isValid() ||
      !moment(endAt, "YYYY-MM-DD HH:mm:ss", true).isValid()
    ) {
      console.error("Invalid range times:", { startAt, endAt })
      return false
    }

    // Check if closures is undefined or not an array
    if (!closures || !Array.isArray(closures) || closures.length === 0) {
      return true
    }

    const startAtMoment = moment(startAt, "YYYY-MM-DD HH:mm:ss")
    const endAtMoment = moment(endAt, "YYYY-MM-DD HH:mm:ss")

    const hasOverlap = closures.some((closure) => {
      // Skip invalid closure objects
      if (!closure || !closure.local_start_time || !closure.local_end_time) {
        return false
      }

      const closureStart = moment(moment(closure.local_start_time).format("YYYY-MM-DD HH:mm:ss"))
      const closureEnd = moment(moment(closure.local_end_time).format("YYYY-MM-DD HH:mm:ss"))

      // Check overlap conditions
      const overlaps =
        closureStart.isBetween(startAtMoment, endAtMoment, null, "[)") ||
        closureEnd.isBetween(startAtMoment, endAtMoment, null, "(]") ||
        (closureStart.isBefore(startAtMoment) && closureEnd.isAfter(endAtMoment)) ||
        (closureStart.isSameOrAfter(startAtMoment) && closureEnd.isSameOrBefore(endAtMoment))

      if (
        overlaps &&
        closure.reopenable_for === closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
      ) {
        setFieldValue("has_call_to_book", true)
        setFieldValue("reopenable_reason", closure.reopenable_reason)
      }

      return overlaps
    })

    return !hasOverlap
  }

  return (
    <FormikProvider value={activityProductInfoForm}>
      <div className={`${styles.productInfoForm}`}>
        {isDefaultView ? (
          <div>
            <div
              className={
                "sm:grid lg:grid-cols-9 md:grid-cols-9 sm:grid-cols-12 md:gap-0 sm:gap-2 min-[1025px]:px-0 min-[426px]:px-4"
              }
            >
              <div className={" lg:col-span-5 md:col-span-5 sm:col-span-6"}>
                <div className={"bg-white sm:mt-0 mt-4 sm:mx-0 mx-4"}>
                  {isProductUnitTypesLoadingValidating || isUnitTypesLabelDataLoading ? (
                    <div className={"text-center"}>
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {selectedProductUnitTypes?.length > 1 ? (
                        <FormGroup
                          label={"Please Select an Available Option"}
                          name={"available_option"}
                          requiredField
                          className={"w-full"}
                        >
                          <MonoSelect
                            {...getFieldProps("available_option")}
                            options={selectedProductUnitTypes}
                            isDisabled={selectedProductUnitTypes?.length === 0}
                            onValueChange={(newValue) => {
                              handleAvailableOption(newValue?.value)
                              setSubmissionError("")
                              setFieldValue("start_time", "")
                              setFieldValue("end_time", "")
                              setFieldValue("end_date", "")
                              setFieldValue("start_date", "")
                              setFieldValue("slot_id", "")
                              setFieldValue("product_availability_id", "")
                              setFieldValue("multi_day_fix_time", "")
                              setFieldValue("reopenable_reason", "")
                              setFieldValue("has_call_to_book", false)
                              setTicketBasedOnMonth({})
                              setMonthAndYears(moment().month() + 1, moment().year())
                              setIsFirstCall(true)
                            }}
                            menuPosition={"fixed"}
                            className={"bg-white"}
                            name={"available_option"}
                            isSearchable={false}
                            isLoading={isProductUnitTypesLoading}
                            isFromWidget
                          />
                        </FormGroup>
                      ) : null}
                      <div>
                        <FormGroup
                          label={
                            unitTypesLabelData?.duration_type
                              ? unitTypesLabelData?.duration_type
                              : "Duration Type"
                          }
                          name={"duration_type"}
                          requiredField
                          className={`w-full ${selectedProductUnitTypes?.length > 1 ? "mt-4" : ""}`}
                        >
                          <MonoSelect
                            options={durationOptions}
                            menuPosition={"fixed"}
                            className={"bg-white"}
                            name={"duration_type"}
                            onValueChange={(newValue) => {
                              previousStartDate = ""
                              setSubmissionError("")
                              setFieldValue("duration_type", newValue?.value)
                              setFieldValue("end_date", "")
                              setFieldValue("start_date", "")
                              setFieldValue("start_time", "")
                              setFieldValue("end_time", "")
                              setFieldValue("slot_id", "")
                              setFieldValue("product_availability_id", "")
                              setFieldValue("multi_day_fix_time", "")
                              setFieldValue("reopenable_reason", "")
                              setFieldValue("quantity", 1)
                              setFieldValue("has_call_to_book", false)
                              setMonthAndYears(moment().month() + 1, moment().year())
                              setTicketBasedOnMonth({})
                              setIsFirstCall(true)
                            }}
                            isDisabled={durationOptions?.length === 0}
                            isSearchable={false}
                            isFromWidget
                          />
                        </FormGroup>
                      </div>
                      {(isSchedulingBasedTicketLoading || isSchedulingBasedTicketValidating) &&
                      isFirstCall ? (
                        <div className={"text-center mt-2"}>
                          <Spinner />
                        </div>
                      ) : (
                        <>
                          <div
                            className={`grid ${
                              values?.available_option?.includes("single_day")
                                ? "grid-cols-1 my-2"
                                : "grid-cols-2 mt-2"
                            } space-x-3`}
                          >
                            <div>
                              <FormGroup
                                label={
                                  values?.available_option === "single_day" ? "Date" : "Start Date"
                                }
                                name={"start_date"}
                                requiredField
                              >
                                <MonoDatePicker
                                  onChange={(selectedDate) => {
                                    setSubmissionError("")
                                    handleStartDateSelection(selectedDate, false)
                                    setFieldValue("reopenable_reason", "")
                                    setFieldValue("has_call_to_book", false)
                                    setFieldValue("quantity", 1)
                                  }}
                                  onCalendarOpen={() => {
                                    const first_availibility_date = checkFirstAvalibilityDate(
                                      selectedDurationDetails?.first_availability_start_date
                                    )
                                    const date = values.start_date
                                      ? values?.start_date
                                      : first_availibility_date || new Date()
                                    setMonthAndYears(moment(date).month() + 1, moment(date).year())
                                  }}
                                  onMonthChange={(date) => {
                                    setMonthAndYears(moment(date).month() + 1, moment(date).year())
                                  }}
                                  inputFieldProps={{
                                    ...getFieldProps("start_date"),
                                    value: values.start_date
                                      ? getDomainBasedDateAndTimeFormat(
                                          values?.start_date,
                                          false,
                                          false
                                        )
                                      : "",
                                    placeholder: "Select Start Date",
                                    className: "bg-white",
                                    color:
                                      errors.start_date && touched.start_date
                                        ? "failure"
                                        : undefined,
                                    icon: CalendarSvg,
                                  }}
                                  minDate={unAvailableDate()}
                                  maxDate={toLocalDate(availabilities?.availability_range?.to_date)}
                                  selected={
                                    values.start_date ? toLocalDate(values.start_date) : null
                                  }
                                  startDate={
                                    values.start_date
                                      ? toLocalDate(values.start_date)
                                      : selectedDurationDetails?.first_availability_start_date
                                        ? checkFirstAvalibilityDate(
                                            selectedDurationDetails?.first_availability_start_date
                                          )
                                        : null
                                  }
                                  endDate={values.end_date ? toLocalDate(values.end_date) : null}
                                  highlightDates={availabilities?.highLightDates}
                                  excludeDates={availabilities?.excludeDates || []}
                                  enableOnBlur={false}
                                  openToDate={
                                    values?.start_date
                                      ? toLocalDate(values?.start_date)
                                      : selectedDurationDetails?.first_availability_start_date
                                        ? checkFirstAvalibilityDate(
                                            selectedDurationDetails?.first_availability_start_date
                                          )
                                        : toLocalDate()
                                  }
                                  isFromWidget
                                  loading={
                                    has(ticketBasedOnMonth, `${selectedMonth}-${selectedYear}`)
                                      ? isSchedulingBasedTicketLoading
                                      : isSchedulingBasedTicketValidating
                                  }
                                  showSpinner
                                  headerContent={
                                    <>
                                      {!availabilities?.highLightDates &&
                                      !isSchedulingBasedTicketLoading ? (
                                        <div
                                          className={
                                            "px-2.5 py-1.5 bg-red-200 text-red-600 text14Regular"
                                          }
                                        >
                                          ! Please select ticket first to see availability.
                                        </div>
                                      ) : null}
                                    </>
                                  }
                                  popperClassName={`!p-0 drop-shadow-lg ${styles.productDatePicker}`}
                                >
                                  <div className={"mt-2 flex items-center gap-2"}>
                                    <span
                                      className={"text13Medium flex items-center text-green-600"}
                                    >
                                      <span className={"mr-1 h-1 w-1 rounded-full bg-green-600"} />
                                      Available
                                    </span>
                                    <span
                                      className={"text13Medium flex items-center text-neutral-500"}
                                    >
                                      <span
                                        className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"}
                                      />
                                      Unavailable
                                    </span>
                                    <span
                                      className={"text13Medium flex items-center text-primary-600"}
                                    >
                                      <span
                                        className={"mr-1 h-1 w-1 rounded-full bg-primary-600 "}
                                      />
                                      Selected
                                    </span>
                                    <span
                                      className={"text13Medium flex items-center text-orange-450"}
                                    >
                                      <span
                                        className={"mr-1 h-1 w-1 rounded-full bg-orange-450 "}
                                      />
                                      Today
                                    </span>
                                  </div>
                                </MonoDatePicker>
                              </FormGroup>
                            </div>
                            {!values?.available_option?.includes("single_day") ? (
                              <div>
                                {selectedDurationDetails &&
                                selectedDurationDetails?.maximum_duration !==
                                  selectedDurationDetails?.minimum_duration ? (
                                  <FormGroup label={"End Date"} name={"end_date"}>
                                    <MonoDatePicker
                                      onChange={onEndDateSelect}
                                      inputFieldProps={{
                                        ...getFieldProps("end_date"),
                                        value: values.end_date
                                          ? getDomainBasedDateAndTimeFormat(
                                              values?.end_date,
                                              false,
                                              false
                                            )
                                          : "",
                                        placeholder: "Select End Date",
                                        className: "bg-white",
                                        color:
                                          errors.end_date && touched.end_date
                                            ? "failure"
                                            : undefined,
                                        icon: CalendarSvg,
                                      }}
                                      onCalendarOpen={() => {
                                        const first_availibility_date = checkFirstAvalibilityDate(
                                          selectedDurationDetails?.first_availability_start_date
                                        )
                                        const date = values.end_date
                                          ? values?.end_date
                                          : values?.start_date
                                            ? values?.start_date
                                            : first_availibility_date || new Date()
                                        setMonthAndYears(
                                          moment(date).month() + 1,
                                          moment(date).year()
                                        )
                                      }}
                                      onMonthChange={(date) => {
                                        setMonthAndYears(
                                          moment(date).month() + 1,
                                          moment(date).year()
                                        )
                                      }}
                                      minDate={
                                        values.start_date
                                          ? toLocalDate(values.start_date)
                                          : unAvailableDate()
                                      }
                                      maxDate={toLocalDate(
                                        availabilities?.availability_range?.to_date
                                      )}
                                      selected={
                                        values.end_date ? toLocalDate(values.end_date) : null
                                      }
                                      highlightDates={endDates?.highLightDates}
                                      excludeDates={endDates?.excludeDates || []}
                                      startDate={
                                        values.start_date
                                          ? toLocalDate(values.start_date)
                                          : selectedDurationDetails?.first_availability_start_date
                                            ? checkFirstAvalibilityDate(
                                                selectedDurationDetails?.first_availability_start_date
                                              )
                                            : getDateInDomainTimezone().toDate()
                                      }
                                      openToDate={
                                        values?.end_date
                                          ? toLocalDate(values?.end_date)
                                          : values?.start_date
                                            ? toLocalDate(values?.start_date)
                                            : selectedDurationDetails?.first_availability_start_date
                                              ? checkFirstAvalibilityDate(
                                                  selectedDurationDetails?.first_availability_start_date
                                                )
                                              : toLocalDate()
                                      }
                                      isFromWidget
                                      loading={
                                        has(ticketBasedOnMonth, `${selectedMonth}-${selectedYear}`)
                                          ? isSchedulingBasedTicketLoading
                                          : isSchedulingBasedTicketValidating
                                      }
                                      showSpinner
                                      popperClassName={`!p-0 drop-shadow-lg ${styles.productDatePicker}`}
                                      headerContent={
                                        <>
                                          {!endDates?.highLightDates &&
                                          !isSchedulingBasedTicketLoading ? (
                                            <div
                                              className={
                                                "px-2.5 py-1.5 bg-red-200 text-red-600 text14Regular"
                                              }
                                            >
                                              ! Please select ticket first to see availability.
                                            </div>
                                          ) : null}
                                        </>
                                      }
                                      enableOnBlur={false}
                                    >
                                      <div className={"mt-2 flex items-center gap-2"}>
                                        <span
                                          className={
                                            "text13Medium flex items-center text-green-600"
                                          }
                                        >
                                          <span
                                            className={"mr-1 h-1 w-1 rounded-full bg-green-600"}
                                          />
                                          Available
                                        </span>
                                        <span
                                          className={
                                            "text13Medium flex items-center text-neutral-500"
                                          }
                                        >
                                          <span
                                            className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"}
                                          />
                                          Unavailable
                                        </span>
                                        <span
                                          className={
                                            "text13Medium flex items-center text-primary-600"
                                          }
                                        >
                                          <span
                                            className={"mr-1 h-1 w-1 rounded-full bg-primary-600 "}
                                          />
                                          Selected
                                        </span>
                                        <span
                                          className={
                                            "text13Medium flex items-center text-orange-450"
                                          }
                                        >
                                          <span
                                            className={"mr-1 h-1 w-1 rounded-full bg-orange-450 "}
                                          />
                                          Today
                                        </span>
                                      </div>
                                    </MonoDatePicker>
                                  </FormGroup>
                                ) : (
                                  <FormGroup label={"End Date"} name={"end_date"} requiredField>
                                    <TextInput
                                      value={
                                        values?.end_date
                                          ? getDomainBasedDateAndTimeFormat(
                                              values?.end_date,
                                              false,
                                              false
                                            )
                                          : ""
                                      }
                                      placeholder={"Select End Date"}
                                      icon={CalendarSvg}
                                      readOnly
                                      color={
                                        errors.businessName && touched.businessName
                                          ? "failure"
                                          : undefined
                                      }
                                      theme={{
                                        field: {
                                          input: {
                                            colors: {
                                              gray: `${textInputTheme?.textInput?.field?.input?.colors?.gray} !border-neutral-450`,
                                            },
                                          },
                                        },
                                      }}
                                    />
                                  </FormGroup>
                                )}
                              </div>
                            ) : null}
                          </div>
                          {selectedDurationDetails?.description && (
                            <div className={"bg-[#F9EDDC] p-2.5 my-2 rounded flex items-center"}>
                              <InfoIcon height={18} width={18} className={"!text-neutral-1000"} />
                              <p className={"text-neutral-1000 text-sm font-medium leading-4 pl-1"}>
                                {selectedDurationDetails?.description}
                              </p>
                            </div>
                          )}
                          {values?.available_option.includes("flex") &&
                          values?.available_option !== "single_day" ? (
                            <>
                              <div className={"grid grid-cols-2 space-x-3"}>
                                <div data-testid={"from-timeslot"}>
                                  <FormGroup
                                    label={"From"}
                                    name={"start_time"}
                                    labelClassName={"block"}
                                    requiredField
                                  >
                                    <MonoSelect
                                      options={getAvailibilitiesTimes?.startTimes}
                                      onValueChange={(newValue: any) => {
                                        setFieldValue("start_time", newValue?.value)
                                        setFieldValue("end_time", "")
                                        setFieldValue(
                                          "product_availability_id",
                                          newValue?.product_availability_id
                                        )
                                        setFieldValue("reopenable_reason", "")
                                        setFieldValue("has_call_to_book", false)
                                      }}
                                      menuPosition={"fixed"}
                                      name={"start_time"}
                                      isLoading={capacityLoading}
                                      isSearchable={false}
                                      isFromWidget
                                    />
                                  </FormGroup>
                                </div>
                                <div data-testid={"to-timeslot"}>
                                  <FormGroup
                                    label={"To"}
                                    name={"end_time"}
                                    labelClassName={"block"}
                                    requiredField
                                  >
                                    <MonoSelect
                                      options={getAvailibilitiesTimes?.endTimes}
                                      onValueChange={(newValue) => {
                                        setFieldValue("end_time", newValue?.value)
                                        setFieldValue("reopenable_reason", "")
                                        setFieldValue("has_call_to_book", false)
                                      }}
                                      menuPosition={"fixed"}
                                      name={"end_time"}
                                      isLoading={capacityLoading}
                                      isSearchable={false}
                                      isFromWidget
                                    />
                                  </FormGroup>
                                </div>
                              </div>
                            </>
                          ) : null}
                          {values?.available_option === "single_day" && values?.start_date ? (
                            <div>
                              <FormGroup
                                label={"Time"}
                                name={"slot_id"}
                                labelClassName={"block"}
                                requiredField
                              >
                                <MonoSelect
                                  {...getFieldProps("slot_id")}
                                  options={singleDayFixedTimeSlot}
                                  onValueChange={(newValue: any) => {
                                    setFieldValue("slot_id", newValue?.value)
                                    setFieldValue(
                                      "product_availability_id",
                                      newValue?.product_availability_id
                                    )
                                    setFieldValue("reopenable_reason", newValue.reopenable_reason)
                                    if (singleDayFixedTimeSlot?.length > 1) {
                                      setFieldValue("quantity", 1)
                                    }
                                    if (
                                      newValue.reopenable_for ===
                                      closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
                                    ) {
                                      setFieldValue("has_call_to_book", true)
                                    } else {
                                      setFieldValue("has_call_to_book", false)
                                    }
                                  }}
                                  menuPosition={"fixed"}
                                  name={"slot_id"}
                                  isLoading={capacityLoading}
                                  isSearchable={false}
                                  isFromWidget
                                />
                              </FormGroup>
                            </div>
                          ) : null}

                          {values?.available_option === "multi_day" &&
                          values?.start_date &&
                          values?.end_date ? (
                            <div className={"mt-2"}>
                              <FormGroup
                                label={"Time"}
                                name={"multi_day_fix_time"}
                                labelClassName={"block"}
                                requiredField
                              >
                                <MonoSelect
                                  {...getFieldProps("multi_day_fix_time")}
                                  options={multiDayFixedTimeSlot}
                                  onValueChange={(newValue: any) => {
                                    setFieldValue("multi_day_fix_time", newValue?.value)
                                    setFieldValue("start_time", newValue?.start_time)
                                    setFieldValue("end_time", newValue?.end_time)
                                    setFieldValue("product_availability_id", newValue?.value)
                                    setFieldValue("reopenable_reason", newValue.reopenable_reason)
                                    if (
                                      newValue.reopenable_for ===
                                      closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
                                    ) {
                                      setFieldValue("has_call_to_book", true)
                                    } else {
                                      setFieldValue("has_call_to_book", false)
                                    }
                                  }}
                                  menuPosition={"fixed"}
                                  name={"multi_day_fix_time"}
                                  isLoading={capacityLoading}
                                  isSearchable={false}
                                  isFromWidget
                                />
                              </FormGroup>
                            </div>
                          ) : null}
                          {!hideQuantityEdit ? (
                            <div className={"my-2"}>{handleTicketCounter()}</div>
                          ) : null}
                        </>
                      )}
                      {values?.has_call_to_book &&
                      (values?.multi_day_fix_time || values?.slot_id || values?.start_time) ? (
                        <div className={"mt-3"}>
                          <div
                            className={
                              "bg-orange-200 border-t border-b border-yellow-400 p-3 flex items-center"
                            }
                          >
                            <div>
                              <PhoneTelephoneIcon className={"text-yellow-600 mt-1.5"} />
                            </div>
                            <div className={"ml-2 text14Medium"}>{values?.reopenable_reason}</div>
                          </div>
                        </div>
                      ) : null}
                      {submissionError && (
                        <div>
                          <Alert color={"failure"}>
                            <span>{submissionError}</span>
                          </Alert>
                        </div>
                      )}
                      {isClosureBetweenDateRange && (
                        <div className={"mt-3"}>
                          <Alert color={"failure"}>
                            <span>
                              The selected date range should not contain product unavailable date
                            </span>
                          </Alert>
                        </div>
                      )}
                      {selectedProductDetail?.minimum_participants_booking_display_message &&
                      liveCapacityData?.length ? (
                        <div
                          className={`${
                            !(values?.quantity >= selectedProductDetail?.minimum_participants)
                              ? "bg-red-200 border-red-500"
                              : "bg-green-200 border-green-500"
                          } border-t border-b p-3 paragraph16Regular mt-2`}
                        >
                          <div className={"flex items-center gap-2"}>
                            <div>
                              <TwoPeopleIcon className={"w-4 h-4"} />
                            </div>
                            <div className={"paragraph14Medium"}>
                              {selectedProductDetail?.minimum_participants_booking_display_message}
                            </div>
                          </div>
                          <div className={"flex items-center gap-1 mt-1"}>
                            <span
                              className={` ${
                                !(values?.quantity >= selectedProductDetail?.minimum_participants)
                                  ? "bg-red-600"
                                  : "bg-green-800"
                              } w-2 h-2 border rounded-full ml-1 mr-2`}
                            />
                            <div
                              className={`text14Medium ${
                                !(values?.quantity >= selectedProductDetail?.minimum_participants)
                                  ? "text-red-600"
                                  : "text-green-800"
                              }`}
                            >{`${values?.quantity} Guests`}</div>
                            <Tooltip
                              content={`There is total ${selectedProductDetail?.minimum_participants} guest required. Minimum ${
                                selectedProductDetail?.minimum_participants
                              } must required. Maximum you can select any number`}
                              trigger={"hover"}
                              className={"text-xs w-64 text-center"}
                            >
                              <InfoIcon className={"w-3.5 h-3.5"} />
                            </Tooltip>
                          </div>
                        </div>
                      ) : null}
                      <div className={"pt-4 hidden sm:block"}>
                        <Button
                          color={"primary"}
                          className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
                          onClick={handleSubmit}
                          disabled={
                            !liveCapacityData[0]?.is_minimum_maximum_requirements_fullfilled ||
                            !isSlotsRequestedForBookingValid() ||
                            // formError ||
                            capacityLoading ||
                            formSubmitting ||
                            Boolean(submissionError) ||
                            Boolean(liveCapacityError) ||
                            Boolean(liveCapacityData[0]?.total_live_capacity === 0) ||
                            isLoading
                          }
                        >
                          {isLoading || capacityLoading ? (
                            <Spinner size={"sm"} light className={"text-primary-400 fill-white"} />
                          ) : (
                            <>
                              Next
                              <RightArrow
                                className={"ml-2.5 text-whiteBlack-100"}
                                width={20}
                                height={20}
                              />
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div
                className={
                  "lg:col-span-4 flex md:col-span-4 sm:col-span-6 justify-center sm:justify-start"
                }
              >
                <div className={"sm:border-r border-neutral-200 lg:pr-8 md:pr-6 sm:pr-3"} />
                <div className={"lg:pl-8 md:pl-6 sm:pl-3 w-full"}>
                  <div
                    className={
                      "bg-white pt-5 pb-6 lg:px-6 md:px-4 px-3 sm:my-0 my-3 sm:mx-0 mx-4 border border-neutral-200 rounded-lg shadow-sm"
                    }
                  >
                    <div className={"flex items-center gap-2 pb-2"}>
                      <span
                        className={
                          " line-clamp-1 lg:text16Medium sm:text14Medium text12Medium lg:w-auto  text-neutral-1000"
                        }
                      >
                        {selectedProductDetail?.name}
                      </span>
                    </div>
                    {liveCapacityData?.length > 0 ? (
                      <div className={"flex flex-col gap-4 mt-1"}>
                        <div className={"w-full items-center"}>
                          <div className={"flex flex-col gap-3"}>
                            <div className={"flex items-center"}>
                              <CalendarIcon className={"mr-2"} />
                              <div className={"text14Regular text-neutral-900"}>
                                {!values?.available_option?.includes("single_day")
                                  ? `${getDomainBasedDateAndTimeFormat(
                                      values?.start_date,
                                      false,
                                      false
                                    )} - ${getDomainBasedDateAndTimeFormat(values?.end_date, false, false)}`
                                  : `${getDomainBasedDateAndTimeFormat(values?.start_date, false, false)}`}
                              </div>
                            </div>
                            <div className={"flex items-center"}>
                              <BillIcon className={"mr-2 text-neutral-400"} />
                              <div className={"text14Regular w-60 text-neutral-900"}>
                                {" "}
                                {handleDayDifference()}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={"w-full border-t border-dashed border-neutral-300"} />
                        <div className={"flex items-center justify-between"}>
                          <div className={"text14Medium"}>
                            {`${liveCapacityData[0]?.formatted_unit_price ?? 0} X ${values.quantity} Qty.`}
                          </div>
                          <div className={"orderTotal"}>
                            {liveCapacityData[0]?.formatted_price ?? 0}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={"text-neutral-700 text14Regular"}>
                        Start to book your ticket from the available options and we will show
                        summary here.
                      </div>
                    )}
                  </div>
                  <div className={"mx-4 pt-1 mb-2 sm:hidden"}>
                    <Button
                      color={"primary"}
                      className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
                      onClick={handleSubmit}
                      disabled={
                        !liveCapacityData[0]?.is_minimum_maximum_requirements_fullfilled ||
                        !isSlotsRequestedForBookingValid() ||
                        // formError ||
                        capacityLoading ||
                        formSubmitting ||
                        Boolean(submissionError) ||
                        Boolean(liveCapacityError) ||
                        Boolean(liveCapacityData[0]?.total_live_capacity === 0) ||
                        isLoading
                      }
                    >
                      {isLoading || capacityLoading ? (
                        <Spinner size={"sm"} light className={"text-primary-400 fill-white"} />
                      ) : (
                        <>
                          Next
                          <RightArrow className={"ml-2.5 text-whiteBlack-100"} />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={"px-4 sm:px-8"}>
            <div
              className={`xl:grid-cols-[720px_396px] lg:grid-cols-5 md:grid md:grid-cols-2 min-[1025px]:px-0 ${isModal ? "" : "justify-center"}`}
            >
              <div className={"md:border-r border-neutral-200 xl:col-span-1 lg:col-span-3 md:pr-8"}>
                <div className={"flex"}>
                  <div className={"w-full"}>
                    {pagePreview === "with_all_details" ? (
                      <>
                        {selectedProductDetail &&
                        selectedProductDetail?.mediaFiles &&
                        selectedProductDetail?.mediaFiles?.length > 0 ? (
                          <MonoImageGallery images={images || []} ContainerClassName={"w-full"} />
                        ) : (
                          <div
                            className={
                              "relative flex h-full min-h-[300px] max-[425px]:min-h-[220px] w-full cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
                            }
                          >
                            <div
                              className={
                                "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                              }
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {selectedProductDetail &&
                        selectedProductDetail?.mediaFiles &&
                        selectedProductDetail?.mediaFiles?.length > 0 ? (
                          <>
                            {images && images.length > 0 && (
                              <>
                                <CustomImage
                                  src={images[0]?.original}
                                  className={"overflow-hidden w-full h-80 rounded-lg"}
                                  fill={false}
                                  height={window.innerWidth <= 900 ? 220 : 360}
                                  width={590}
                                />
                                <div className={`mt-3 grid grid-cols-2 gap-3`}>
                                  {(images?.slice(1) || []).map((image: any, index) => {
                                    return (
                                      <div key={index}>
                                        <CustomImage
                                          src={image.original}
                                          className={
                                            "object-cover object-center h-44 w-full rounded-lg"
                                          }
                                          fill={false}
                                          height={170}
                                          width={287}
                                        />
                                      </div>
                                    )
                                  })}
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <div
                            className={
                              "relative flex h-full min-h-[300px] max-[425px]:min-h-[220px] w-full cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
                            }
                          >
                            <div
                              className={
                                "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                              }
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {pagePreview === "with_all_details" && (
                  <Button
                    color={"primary"}
                    className={"w-full mt-4 md:hidden !h-10"}
                    onClick={handleScrollInToViewFunction}
                  >
                    Book Now
                  </Button>
                )}
                {pagePreview === "with_all_details" && (
                  <div>
                    {locationData?.length > 0 ? (
                      <div className={"mt-2"}>
                        <div className={"w-full border-b border-neutral-200"} />
                        {locationData?.map((location, index) => {
                          return (
                            <div className={"my-3 flex"} key={index}>
                              {location.type === "office" ? (
                                <div className={"mb-4 sm:mb-0"}>
                                  <LocationBuildings className={"mr-1 text-primary-600"} />
                                </div>
                              ) : (
                                <div className={"mb-8 sm:mb-0"}>
                                  <LocationMark className={"mr-1 text-primary-600"} />
                                </div>
                              )}
                              <div className={"text14Medium text-primary-600"}>
                                <ProductLocation location={location} />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : null}
                    <div className={"w-full border-b border-neutral-200 my-3"} />
                    <div className={"mt-2"}>
                      <DangerouslyHTMLTextPriview
                        content={selectedProductDetail?.description?.toString() ?? ""}
                        className={"paragraph14Regular"}
                      />
                    </div>
                    {selectedProductDetail?.cancellation_policy ? (
                      <div
                        className={
                          "flex items-center justify-start rounded-lg bg-[#F9EDDC] p-4 mt-4"
                        }
                      >
                        <div className={"flex flex-col gap-1"}>
                          <span
                            className={"text14Semibold text-neutral-1000 flex items-center gap-1"}
                          >
                            <span>
                              <LifePreServer />
                            </span>
                            Cancellation Policy
                          </span>
                          <DangerouslyHTMLTextPriview
                            content={selectedProductDetail?.cancellation_policy?.toString() ?? ""}
                            className={
                              "!paragraph14Regular text-neutral-800 [&>p>span]:!paragraph14Regular [&>p>span]:!text-neutral-800"
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                    {selectedProductDetail?.terms_and_conditions ? (
                      <div
                        className={
                          "mt-4 flex items-center justify-start rounded-lg bg-[#FCEBEA] p-4"
                        }
                      >
                        <div className={"flex flex-col gap-1"}>
                          <span
                            className={"text14Semibold text-neutral-1000 flex items-center gap-1"}
                          >
                            <span>
                              <LifePreServer />
                            </span>
                            Terms and Conditions
                          </span>
                          <DangerouslyHTMLTextPriview
                            content={selectedProductDetail?.terms_and_conditions?.toString() ?? ""}
                            className={
                              "!paragraph14Regular text-neutral-800 [&>p>span]:!paragraph14Regular [&>p>span]:!text-neutral-800"
                            }
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              <div
                className={`w-full xl:col-span-1 lg:col-span-2 md:pl-8 md:sticky ${isFullPage ? "top-[85px]" : "top-0"} self-start`}
                id={"booking-container"}
              >
                <div className={"flex justify-center"}>
                  <div
                    className={
                      "bg-white md:mt-0 mt-4 border border-neutral-200 rounded-xl md:p-5 sm:p-4 p-3.5 w-full shadow-lg"
                    }
                  >
                    {isProductUnitTypesLoadingValidating || isUnitTypesLabelDataLoading ? (
                      <div className={"text-center"}>
                        <Spinner />
                      </div>
                    ) : (
                      <>
                        {selectedProductUnitTypes?.length > 1 ? (
                          <FormGroup
                            label={"Please Select an Available Option"}
                            name={"available_option"}
                            requiredField
                            className={"w-full"}
                          >
                            <MonoSelect
                              {...getFieldProps("available_option")}
                              options={selectedProductUnitTypes}
                              onValueChange={(newValue) => {
                                handleAvailableOption(newValue?.value)
                                setSubmissionError("")
                                setFieldValue("start_time", "")
                                setFieldValue("end_time", "")
                                setFieldValue("end_date", "")
                                setFieldValue("start_date", "")
                                setFieldValue("slot_id", "")
                                setFieldValue("product_availability_id", "")
                                setFieldValue("multi_day_fix_time", "")
                                setIsFirstCall(true)
                                setFieldValue("reopenable_reason", "")
                                setFieldValue("has_call_to_book", false)
                                setMonthAndYears(moment().month() + 1, moment().year())
                                setTicketBasedOnMonth({})
                              }}
                              isDisabled={selectedProductUnitTypes?.length === 0}
                              menuPosition={"fixed"}
                              className={`bg-white`}
                              name={"available_option"}
                              isLoading={isProductUnitTypesLoading}
                              isSearchable={false}
                              isFromWidget
                            />
                          </FormGroup>
                        ) : null}
                        <div className={"mt-2"}>
                          <FormGroup
                            label={
                              unitTypesLabelData?.duration_type
                                ? unitTypesLabelData?.duration_type
                                : "Duration Type"
                            }
                            name={"duration_type"}
                            requiredField
                            className={selectedProductUnitTypes?.length > 1 ? "w-full" : "w-full"}
                          >
                            <MonoSelect
                              {...getFieldProps("duration_type")}
                              options={durationOptions}
                              menuPosition={"fixed"}
                              className={"bg-white"}
                              name={"duration_type"}
                              isDisabled={durationOptions?.length === 0}
                              onValueChange={(newValue) => {
                                setSubmissionError("")
                                setFieldValue("end_date", "")
                                setFieldValue("start_date", "")
                                setFieldValue("start_time", "")
                                setFieldValue("end_time", "")
                                setFieldValue("slot_id", "")
                                setFieldValue("product_availability_id", "")
                                setFieldValue("multi_day_fix_time", "")
                                setFieldValue("reopenable_reason", "")
                                setFieldValue("has_call_to_book", false)
                                setFieldValue("quantity", 1)
                                setIsFirstCall(true)
                                setMonthAndYears(moment().month() + 1, moment().year())
                                setTicketBasedOnMonth({})
                              }}
                              isSearchable={false}
                              isFromWidget
                            />
                          </FormGroup>
                        </div>
                        {(isSchedulingBasedTicketLoading || isSchedulingBasedTicketValidating) &&
                        isFirstCall ? (
                          <div className={"text-center mt-2"}>
                            <Spinner />
                          </div>
                        ) : (
                          <>
                            <div
                              className={`grid ${
                                values?.available_option?.includes("single_day")
                                  ? "grid-cols-1 my-2"
                                  : "sm:grid-cols-2 grid-cols-1 sm:space-y-0 space-y-3 mt-2 sm:space-x-3"
                              }`}
                            >
                              <div>
                                <FormGroup
                                  label={
                                    values?.available_option === "single_day"
                                      ? "Date"
                                      : "Start Date"
                                  }
                                  name={"start_date"}
                                  requiredField
                                >
                                  <MonoDatePicker
                                    onChange={(selectedDate) => {
                                      handleStartDateSelection(selectedDate, false)
                                    }}
                                    inputFieldProps={{
                                      ...getFieldProps("start_date"),
                                      value: values.start_date
                                        ? getDomainBasedDateAndTimeFormat(
                                            values?.start_date,
                                            false,
                                            false
                                          )
                                        : "",
                                      placeholder: "Select Start Date",
                                      className: "bg-white",
                                      color:
                                        errors.start_date && touched.start_date
                                          ? "failure"
                                          : undefined,
                                      icon: CalendarSvg,
                                    }}
                                    onCalendarOpen={() => {
                                      const first_availibility_date = checkFirstAvalibilityDate(
                                        selectedDurationDetails?.first_availability_start_date
                                      )
                                      const date = values.start_date
                                        ? values?.start_date
                                        : first_availibility_date || new Date()
                                      setMonthAndYears(
                                        moment(date).month() + 1,
                                        moment(date).year()
                                      )
                                    }}
                                    onMonthChange={(date) => {
                                      setMonthAndYears(
                                        moment(date).month() + 1,
                                        moment(date).year()
                                      )
                                    }}
                                    minDate={unAvailableDate()}
                                    maxDate={toLocalDate(
                                      availabilities?.availability_range?.to_date
                                    )}
                                    selected={
                                      values.start_date ? toLocalDate(values.start_date) : null
                                    }
                                    startDate={
                                      values.start_date
                                        ? toLocalDate(values.start_date)
                                        : selectedDurationDetails?.first_availability_start_date
                                          ? checkFirstAvalibilityDate(
                                              selectedDurationDetails?.first_availability_start_date
                                            )
                                          : null
                                    }
                                    endDate={values.end_date ? toLocalDate(values.end_date) : null}
                                    highlightDates={availabilities?.highLightDates}
                                    excludeDates={availabilities?.excludeDates || []}
                                    enableOnBlur={false}
                                    openToDate={
                                      values?.start_date
                                        ? toLocalDate(values?.start_date)
                                        : selectedDurationDetails?.first_availability_start_date
                                          ? checkFirstAvalibilityDate(
                                              selectedDurationDetails?.first_availability_start_date
                                            )
                                          : toLocalDate()
                                    }
                                    isFromWidget
                                    popperProps={{
                                      strategy: "fixed",
                                    }}
                                    loading={
                                      has(ticketBasedOnMonth, `${selectedMonth}-${selectedYear}`)
                                        ? isSchedulingBasedTicketLoading
                                        : isSchedulingBasedTicketValidating
                                    }
                                    showSpinner
                                    headerContent={
                                      <>
                                        {!availabilities?.highLightDates &&
                                        !isSchedulingBasedTicketLoading ? (
                                          <div
                                            className={
                                              "px-2.5 py-1.5 bg-red-200 text-red-600 text14Regular"
                                            }
                                          >
                                            ! Please select ticket first to see availability.
                                          </div>
                                        ) : null}
                                      </>
                                    }
                                    popperClassName={`!p-0 drop-shadow-lg ${styles.productDatePicker}`}
                                  >
                                    <div className={"mt-2 flex items-center gap-2"}>
                                      <span
                                        className={"text13Medium flex items-center text-green-600"}
                                      >
                                        <span
                                          className={"mr-1 h-1 w-1 rounded-full bg-green-600"}
                                        />
                                        Available
                                      </span>
                                      <span
                                        className={
                                          "text13Medium flex items-center text-neutral-500"
                                        }
                                      >
                                        <span
                                          className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"}
                                        />
                                        Unavailable
                                      </span>
                                      <span
                                        className={
                                          "text13Medium flex items-center text-primary-600"
                                        }
                                      >
                                        <span
                                          className={"mr-1 h-1 w-1 rounded-full bg-primary-600 "}
                                        />
                                        Selected
                                      </span>
                                      <span
                                        className={"text13Medium flex items-center text-orange-450"}
                                      >
                                        <span
                                          className={"mr-1 h-1 w-1 rounded-full bg-orange-450 "}
                                        />
                                        Today
                                      </span>
                                    </div>
                                  </MonoDatePicker>
                                </FormGroup>
                              </div>
                              {!values?.available_option?.includes("single_day") ? (
                                <div>
                                  {selectedDurationDetails?.maximum_duration !==
                                  selectedDurationDetails?.minimum_duration ? (
                                    <FormGroup label={"End Date"} name={"end_date"}>
                                      <MonoDatePicker
                                        onChange={onEndDateSelect}
                                        inputFieldProps={{
                                          ...getFieldProps("end_date"),
                                          value: values.end_date
                                            ? getDomainBasedDateAndTimeFormat(
                                                values?.end_date,
                                                false,
                                                false
                                              )
                                            : "",
                                          placeholder: "Select End Date",
                                          className: "bg-white",
                                          color:
                                            errors.end_date && touched.end_date
                                              ? "failure"
                                              : undefined,
                                          icon: CalendarSvg,
                                        }}
                                        openToDate={
                                          values?.end_date
                                            ? toLocalDate(values?.end_date)
                                            : values?.start_date
                                              ? toLocalDate(values?.start_date)
                                              : selectedDurationDetails?.first_availability_start_date
                                                ? checkFirstAvalibilityDate(
                                                    selectedDurationDetails?.first_availability_start_date
                                                  )
                                                : toLocalDate()
                                        }
                                        minDate={
                                          values.start_date
                                            ? toLocalDate(values.start_date)
                                            : unAvailableDate()
                                        }
                                        maxDate={toLocalDate(
                                          availabilities?.availability_range?.to_date
                                        )}
                                        selected={
                                          values.end_date ? toLocalDate(values.end_date) : null
                                        }
                                        highlightDates={endDates?.highLightDates}
                                        excludeDates={endDates?.excludeDates || []}
                                        popperProps={{
                                          strategy: "fixed",
                                        }}
                                        startDate={
                                          values.start_date
                                            ? toLocalDate(values.start_date)
                                            : selectedDurationDetails?.first_availability_start_date
                                              ? checkFirstAvalibilityDate(
                                                  selectedDurationDetails?.first_availability_start_date
                                                )
                                              : getDateInDomainTimezone().toDate()
                                        }
                                        onCalendarOpen={() => {
                                          const first_availibility_date = checkFirstAvalibilityDate(
                                            selectedDurationDetails?.first_availability_start_date
                                          )
                                          const date = values.end_date
                                            ? values?.end_date
                                            : values?.start_date
                                              ? values?.start_date
                                              : first_availibility_date || new Date()
                                          setMonthAndYears(
                                            moment(date).month() + 1,
                                            moment(date).year()
                                          )
                                        }}
                                        onMonthChange={(date) => {
                                          setMonthAndYears(
                                            moment(date).month() + 1,
                                            moment(date).year()
                                          )
                                        }}
                                        endDate={
                                          values.end_date ? toLocalDate(values.end_date) : null
                                        }
                                        isFromWidget
                                        popperClassName={`!p-0 drop-shadow-lg ${styles.productDatePicker}`}
                                        loading={
                                          has(
                                            ticketBasedOnMonth,
                                            `${selectedMonth}-${selectedYear}`
                                          )
                                            ? isSchedulingBasedTicketLoading
                                            : isSchedulingBasedTicketValidating
                                        }
                                        showSpinner
                                        headerContent={
                                          <>
                                            {!endDates?.highLightDates &&
                                            !isSchedulingBasedTicketLoading ? (
                                              <div
                                                className={
                                                  "px-2.5 py-1.5 bg-red-200 text-red-600 text14Regular"
                                                }
                                              >
                                                ! Please select ticket first to see availability.
                                              </div>
                                            ) : null}
                                          </>
                                        }
                                        enableOnBlur={false}
                                      >
                                        <div className={"mt-2 flex items-center gap-2"}>
                                          <span
                                            className={
                                              "text13Medium flex items-center text-green-600"
                                            }
                                          >
                                            <span
                                              className={"mr-1 h-1 w-1 rounded-full bg-green-600"}
                                            />
                                            Available
                                          </span>
                                          <span
                                            className={
                                              "text13Medium flex items-center text-neutral-500"
                                            }
                                          >
                                            <span
                                              className={"mr-1 h-1 w-1 rounded-full bg-neutral-500"}
                                            />
                                            Unavailable
                                          </span>
                                          <span
                                            className={
                                              "text13Medium flex items-center text-primary-600"
                                            }
                                          >
                                            <span
                                              className={
                                                "mr-1 h-1 w-1 rounded-full bg-primary-600 "
                                              }
                                            />
                                            Selected
                                          </span>
                                          <span
                                            className={
                                              "text13Medium flex items-center text-orange-450"
                                            }
                                          >
                                            <span
                                              className={"mr-1 h-1 w-1 rounded-full bg-orange-450 "}
                                            />
                                            Today
                                          </span>
                                        </div>
                                      </MonoDatePicker>
                                    </FormGroup>
                                  ) : (
                                    <FormGroup label={"End Date"} name={"end_date"} requiredField>
                                      <TextInput
                                        value={
                                          values?.end_date
                                            ? getDomainBasedDateAndTimeFormat(
                                                values?.end_date,
                                                false,
                                                false
                                              )
                                            : ""
                                        }
                                        placeholder={"Select End Date"}
                                        icon={CalendarSvg}
                                        readOnly
                                        color={
                                          errors.businessName && touched.businessName
                                            ? "failure"
                                            : undefined
                                        }
                                        theme={{
                                          field: {
                                            input: {
                                              colors: {
                                                gray: `${textInputTheme?.textInput?.field?.input?.colors?.gray} !border-neutral-450`,
                                              },
                                            },
                                          },
                                        }}
                                      />
                                    </FormGroup>
                                  )}
                                </div>
                              ) : null}
                            </div>
                            {selectedDurationDetails?.description && (
                              <div className={"bg-[#F9EDDC] p-2.5 my-2 rounded flex items-center"}>
                                <InfoIcon height={14} width={14} className={"text-neutral-1000"} />
                                <p className={"text-neutral-1000 text14Medium leading-4 pl-1"}>
                                  {selectedDurationDetails?.description}
                                </p>
                              </div>
                            )}
                            {values?.available_option.includes("flex") &&
                            values?.available_option !== "single_day" ? (
                              <>
                                <div className={"grid grid-cols-2 space-x-3"}>
                                  <div>
                                    <FormGroup
                                      label={"From"}
                                      name={"start_time"}
                                      labelClassName={"block"}
                                      requiredField
                                    >
                                      <MonoSelect
                                        options={getAvailibilitiesTimes?.startTimes}
                                        onValueChange={(newValue: any) => {
                                          setFieldValue("start_time", newValue?.value)
                                          setFieldValue("end_time", "")
                                          setFieldValue(
                                            "product_availability_id",
                                            newValue?.product_availability_id
                                          )
                                          setFieldValue("reopenable_reason", "")
                                          setFieldValue("has_call_to_book", false)
                                        }}
                                        menuPosition={"fixed"}
                                        name={"start_time"}
                                        isLoading={capacityLoading}
                                        isSearchable={false}
                                        isFromWidget
                                      />
                                    </FormGroup>
                                  </div>
                                  <div>
                                    <FormGroup
                                      label={"To"}
                                      name={"end_time"}
                                      labelClassName={"block"}
                                      requiredField
                                    >
                                      <MonoSelect
                                        options={getAvailibilitiesTimes?.endTimes}
                                        onValueChange={(newValue) => {
                                          setFieldValue("end_time", newValue?.value)
                                          setFieldValue("reopenable_reason", "")
                                          setFieldValue("has_call_to_book", false)
                                        }}
                                        menuPosition={"fixed"}
                                        name={"end_time"}
                                        isLoading={capacityLoading}
                                        isSearchable={false}
                                        isFromWidget
                                      />
                                    </FormGroup>
                                  </div>
                                </div>
                              </>
                            ) : null}
                            {values?.available_option === "single_day" ? (
                              <div>
                                <FormGroup
                                  label={"Time"}
                                  name={"slot_id"}
                                  labelClassName={"block"}
                                  requiredField
                                >
                                  <MonoSelect
                                    {...getFieldProps("slot_id")}
                                    options={singleDayFixedTimeSlot}
                                    onValueChange={(newValue: any) => {
                                      setFieldValue("slot_id", newValue?.value)
                                      setFieldValue(
                                        "product_availability_id",
                                        newValue?.product_availability_id
                                      )
                                      setFieldValue("reopenable_reason", newValue.reopenable_reason)
                                      if (singleDayFixedTimeSlot?.length > 1) {
                                        setFieldValue("quantity", 1)
                                      }
                                      if (
                                        newValue.reopenable_for ===
                                        closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
                                      ) {
                                        setFieldValue("has_call_to_book", true)
                                      } else {
                                        setFieldValue("has_call_to_book", false)
                                      }
                                    }}
                                    menuPosition={"fixed"}
                                    name={"slot_id"}
                                    isLoading={capacityLoading}
                                    isSearchable={false}
                                    isFromWidget
                                  />
                                </FormGroup>
                              </div>
                            ) : null}

                            {values?.available_option === "multi_day" ? (
                              <div className={"mt-2"}>
                                <FormGroup
                                  label={"Time"}
                                  name={"multi_day_fix_time"}
                                  labelClassName={"block"}
                                  requiredField
                                >
                                  <MonoSelect
                                    {...getFieldProps("multi_day_fix_time")}
                                    options={multiDayFixedTimeSlot}
                                    onValueChange={(newValue: any) => {
                                      setFieldValue("multi_day_fix_time", newValue?.value)
                                      setFieldValue("start_time", newValue?.start_time)
                                      setFieldValue("end_time", newValue?.end_time)
                                      setFieldValue("product_availability_id", newValue?.value)
                                      setFieldValue("reopenable_reason", newValue.reopenable_reason)
                                      if (
                                        newValue.reopenable_for ===
                                        closureType.CLOSED_EXTERNAL_CALL_TO_BOOK_AND_OPEN_INTERNAL
                                      ) {
                                        setFieldValue("has_call_to_book", true)
                                      } else {
                                        setFieldValue("has_call_to_book", false)
                                      }
                                    }}
                                    menuPosition={"fixed"}
                                    name={"multi_day_fix_time"}
                                    isLoading={capacityLoading}
                                    isSearchable={false}
                                    isFromWidget
                                  />
                                </FormGroup>
                              </div>
                            ) : null}
                            {!hideQuantityEdit ? (
                              <div className={"mt-2"}>{handleTicketCounter()}</div>
                            ) : null}
                          </>
                        )}
                        {values?.has_call_to_book &&
                        (values?.multi_day_fix_time || values?.slot_id || values?.start_time) ? (
                          <div className={"mt-3"}>
                            <div
                              className={
                                "bg-orange-200 border-t border-b border-yellow-400 p-3 flex items-center"
                              }
                            >
                              <div>
                                <PhoneTelephoneIcon className={"text-yellow-600 mt-1.5"} />
                              </div>
                              <div className={"ml-2 text14Medium"}>{values?.reopenable_reason}</div>
                            </div>
                          </div>
                        ) : null}
                        {submissionError && (
                          <div className={"my-2"}>
                            <Alert color={"failure"}>
                              <span>{submissionError}</span>
                            </Alert>
                          </div>
                        )}
                        {isClosureBetweenDateRange && (
                          <div className={"my-2"}>
                            <Alert color={"failure"}>
                              <span>
                                Please choose the date without range of unavailable product dates
                              </span>
                            </Alert>
                          </div>
                        )}
                        {selectedProductDetail?.minimum_participants_booking_display_message &&
                        liveCapacityData?.length ? (
                          <div
                            className={`${
                              !(values?.quantity >= selectedProductDetail?.minimum_participants)
                                ? "bg-red-200 border-red-500"
                                : "bg-green-200 border-green-500"
                            } border-t border-b p-3 paragraph16Regular mt-2`}
                          >
                            <div className={"flex items-center gap-2"}>
                              <div>
                                <TwoPeopleIcon className={"w-4 h-4"} />
                              </div>
                              <div className={"paragraph14Medium"}>
                                {
                                  selectedProductDetail?.minimum_participants_booking_display_message
                                }
                              </div>
                            </div>
                            <div className={"flex items-center gap-1 mt-1"}>
                              <span
                                className={` ${
                                  !(values?.quantity >= selectedProductDetail?.minimum_participants)
                                    ? "bg-red-600"
                                    : "bg-green-800"
                                } w-2 h-2 border rounded-full ml-1 mr-2`}
                              />
                              <div
                                className={`text14Medium ${
                                  !(values?.quantity >= selectedProductDetail?.minimum_participants)
                                    ? "text-red-600"
                                    : "text-green-800"
                                }`}
                              >{`${values?.quantity} Guests`}</div>
                              <Tooltip
                                content={`There is total ${selectedProductDetail?.minimum_participants} guest required. Minimum ${selectedProductDetail?.minimum_participants} must required. Maximum you can select any number`}
                                trigger={"hover"}
                                className={"text-xs w-64 text-center"}
                              >
                                <InfoIcon className={"w-3.5 h-3.5"} />
                              </Tooltip>
                            </div>
                          </div>
                        ) : null}
                        <div className={"pt-2"}>
                          <Button
                            color={"primary"}
                            className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
                            onClick={handleSubmit}
                            size={"sm"}
                            disabled={
                              !liveCapacityData[0]?.is_minimum_maximum_requirements_fullfilled ||
                              !isSlotsRequestedForBookingValid() ||
                              // formError ||
                              capacityLoading ||
                              formSubmitting ||
                              Boolean(submissionError) ||
                              Boolean(liveCapacityError) ||
                              Boolean(liveCapacityData[0]?.total_live_capacity === 0) ||
                              isLoading
                            }
                          >
                            {isLoading || capacityLoading ? (
                              <Spinner
                                size={"xs"}
                                light
                                className={"text-primary-400 fill-white"}
                              />
                            ) : (
                              <>
                                Next
                                <RightArrow
                                  className={"ml-2.5 text-whiteBlack-100"}
                                  width={20}
                                  height={20}
                                />
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormikProvider>
  )
}
export default RentalProductDetailsPreview
